import {
  THREE,
  app,
  CourseLight,
  CourseKeyboardControls,
  GolfBall,
  UnitConversions,
  MeshLoader,
  YardageLinesMaterial,
  generateSetupData,
  CoursePlayer,
  FuseRenderer,
  FuseXRSession,
  AnchorController,
  XRShotControls,
  WorldMask,
  ShotStatsPanel,
  GroundStatsHUD,
  HeadLockedHint,
  ContactShadow,
  AudioPlayer,
  ImpactSound,
  WindAmbience,
  FlagStick,
  WristMenu,
  SkyDome,
  ShotTv,
  InfoPanel,
  ShotPerspectiveCamera,
  MeshStandardNodeMaterial,
  XRSwingControls,
  ActionMenu,
  StickRepeat,
  CalibrationStore,
  resolveProfileCode,
  ClubOverlay,
  ControllerRays,
  InputModalityDetector,
  RoomBounds,
  ATTACHMENTS,
  VIRTUAL_CLUBS,
  CAPTURE_RADIUS,
  deriveShot,
  syntheticImpact,
  setViewerPosition,
} from '@opengolfsim/fuse';
import type { InfoPage, WristMenuStatus, AttachmentId, Impact, ClubSpec, WindowRect, ImpactVoice } from '@opengolfsim/fuse';
import rangeMtnsModel from '../range/models/rangeMtns.glb?url';
import fairwayTexture from '../range/textures/gen_fairway_tex.png?url';
import fairwayMap from '../range/textures/gen_fairway_map.png?url';

const sunColor = new THREE.Color('#fcfae9');
const mountainColor = new THREE.Color('#687e80');
const hashMarks = [50, 100, 150, 200, 250, 300];

/** FUSE Link serves the app and this loopback socket on the headset. */
const DEFAULT_BRIDGE_URL = 'ws://127.0.0.1:9210/ws';

/**
 * Ball speed (mph) above which the chase-cam TV appears — roughly the 100 yd
 * mark ('8 Iron' qualifies, 'Wedge' doesn't). It has to be a launch-time
 * estimate: the physics is a forward integrator with no lookahead, and
 * stats.carry is only "distance so far", not crossing 100 yd until well over
 * halfway through a driver's flight. Expect to tune this on the mat.
 */
const TV_MIN_BALL_SPEED_MPH = 80;

/**
 * Practice preset the MR page starts on. A short chip is what's useful on an
 * indoor mat; set here rather than by reordering SHOT_PRESETS, which is public
 * API and also maps the desktop number keys by array position.
 */
const DEFAULT_PRESET = 'Chip';

/**
 * Club the swing mode starts on. A mid iron is the most forgiving thing to
 * calibrate and swing indoors — a driver's arc wants more room than most rooms
 * have.
 */
const DEFAULT_SWING_CLUB = '7 Iron';

/** The two input worlds this page supports. Default is the launch monitor. */
type RangeMode = 'monitor' | 'swing';

const gameContext: {
  startPoint: THREE.Vector3,
  aimPoint: THREE.Vector3,
  currentPlayer?: CoursePlayer,

  timer: THREE.Timer,
  scene?: THREE.Scene,
  renderer?: FuseRenderer,
  golfBall?: GolfBall,
  lightGroup?: CourseLight,
  ground?: THREE.Mesh,
  groundLines?: THREE.Mesh,
  mountain?: THREE.Object3D,
  yardageLines?: YardageLinesMaterial,
  meshLoader?: MeshLoader,
  setupData?: OpenGolfSim.SetupData,

  // MR
  worldRoot?: THREE.Group,
  camera?: THREE.PerspectiveCamera,
  xrSession?: FuseXRSession,
  anchorController?: AnchorController,
  xrControls?: XRShotControls,
  worldMask?: WorldMask,
  statsPanel?: ShotStatsPanel,
  groundHud?: GroundStatsHUD,
  hint?: HeadLockedHint,
  contactShadow?: ContactShadow,
  wristMenu?: WristMenu,
  controls?: CourseKeyboardControls,
  skyDome?: SkyDome,
  shotTv?: ShotTv,
  infoPanel?: InfoPanel,
  chaseCamera?: ShotPerspectiveCamera,

  // Virtual golf (controller swing) mode — everything here is inert in 'monitor'
  swingControls?: XRSwingControls,
  clubOverlay?: ClubOverlay,
  /** The translucent club the player lines their real attachment up with. */
  clubGhost?: ClubOverlay,
  controllerRays?: ControllerRays,
  /** Watches which device is actually in use and drives auto mode selection. */
  modeDetector?: InputModalityDetector,
  /** ?mode= was given: the auto-switcher is disabled for the whole session. */
  modeForced: boolean,

  /** Resolves the MR window (walls/ceiling) from Space Setup planes. */
  roomBounds?: RoomBounds,
  /** 'auto' frames from detected planes; 'manual' uses windowRect; 'off' = infinite. */
  windowMode: 'auto' | 'manual' | 'off',
  windowRect?: WindowRect,
  /** The auto solve landed for the current anchor — stop polling. */
  windowSolved: boolean,
  /** Yaw from the anchored event, needed by the plane solve. */
  anchorYaw: number,

  /** ?wind=mph[,deg] — deg 0 = tailwind, 180 = into, 90 = pushes right. */
  windMph: number,
  windDeg: number,

  impactSound?: ImpactSound,
  windAmbience?: WindAmbience,
  flagSticks?: FlagStick[],
  /** Set by launchSwing so launchShot knows which club voice to play. */
  lastImpactVoice?: ImpactVoice,
  /** The large controller-mode menu. Replaces the wrist panel when B is held. */
  actionMenu?: ActionMenu,
  /**
   * Immersive mode only: the stick is moving the tee. 'move' slides it, 'aim'
   * swings the target line — one stick, three degrees of freedom, so the stick
   * click switches between them as it does in the club refinement.
   */
  teeAdjust?: 'move' | 'aim',
  teeStick?: StickRepeat,
  /** Club calibrations: localStorage always, D1 too when a profile code exists. */
  calibrationStore?: CalibrationStore,
  /** Which panel a controller ray is currently resting on, if any. */
  rayTarget?: 'menu' | 'info' | 'actions',
  swingClubName: string,
  swingAttachment: AttachmentId,
  /** ?attach=<metres> — how far behind the controller the grip's top sits. */
  swingButtOffset?: number,
  /** ?assist=0..1 — shot-shaping assist (face, attack, loft, strike). */
  swingAssist: number,
  /** ?power=0.5..1.6 — ball-speed (smash) multiplier on top of the physics. */
  swingPower: number,
  /** ?shape=0..3 — curvature responsiveness; undefined = the model's default. */
  swingShape?: number,
  /** ?clubgain= — scales the per-club speed assist; undefined = the tuned bag. */
  swingClubGain?: number,
  /** ?clubaxis= / ?clubface= — grip-space overrides if calibration proves awkward. */
  swingShaftAxis?: THREE.Vector3,
  swingFaceAxis?: THREE.Vector3,
  swingCaptureRadius: number,
  swingDebug: boolean,
  /** Which controller carries the club — the YEZRO mounts on the right. */
  clubHand: 'left' | 'right',

  /** The bridge URL actually in use — shown on the help panel. */
  bridgeUrl: string,
  /**
   * Latched once the bridge socket has opened at least once. AppBridge emits
   * bridgeStatus(false) on a failed first attempt exactly as it does on a drop,
   * so "never connected" is only distinguishable by remembering.
   */
  bridgeEverConnected: boolean,

  /**
   * Tee and aim in reference space (identity-equal to the sim frame until the
   * world is anchored). The chase camera is unparented, so it must be fed
   * world-space poses — Object3D.lookAt treats its target as world space,
   * which would silently misaim a camera positioned in worldRoot-local space.
   */
  teeWorld: THREE.Vector3,
  aimWorld: THREE.Vector3,
  /** 'auto' gates on ball speed, 'always' shows every shot, 'off' disables. */
  tvMode: 'auto' | 'always' | 'off',
  /** URL-requested mode, restored when the wrist-menu toggle comes back on. */
  tvModeFromUrl: 'auto' | 'always' | 'off',
  /** A qualifying shot is in the air — the offscreen pass is running. */
  tvChasing: boolean,
  tvFrame: number,
  /** Wrist-menu placement, ?wrist= / ?wristdistal= so it's tunable on the mat. */
  wristPalmOffset?: number,
  wristDistalOffset?: number,

  mode: RangeMode,
  simMode: boolean,
  /**
   * ?vr=1 — immersive-vr instead of passthrough. No portal, no room framing and
   * no pinch-to-place flow, since none of the three have anything to act on
   * once the room is gone. Also the only mode with a chance of running outside
   * Quest: Safari on visionOS offers immersive-vr and not immersive-ar.
   */
  immersive: boolean,
  maskMode: 'punch' | 'dither',
  confirmRequested: boolean,
  bridgeConnected: boolean,
  /** A real launch-monitor shot has arrived this session. */
  monitorLive: boolean,
  /** ?testshots=1|0 — force practice shots on/off regardless of monitor. */
  testShotsOverride?: boolean,
  pendingShot?: OpenGolfSim.Shot,
  reanchorArmedUntil?: number,
  audioPlayer?: AudioPlayer,
  handedSide: 1 | -1,
  perfHud: boolean,
  frameEmaMs: number,
  lastPerfCheckAt: number,
  foveation: number,
  presetName: string,
} = {
  timer: new THREE.Timer(),
  startPoint: new THREE.Vector3(0, 0, 0),
  aimPoint: new THREE.Vector3(0, 0, 200),
  teeWorld: new THREE.Vector3(0, 0, 0),
  aimWorld: new THREE.Vector3(0, 0, 200),
  bridgeUrl: DEFAULT_BRIDGE_URL,
  bridgeEverConnected: false,
  tvMode: 'auto',
  tvModeFromUrl: 'auto',
  tvChasing: false,
  tvFrame: 0,
  mode: 'monitor',
  modeForced: false,
  windowMode: 'auto',
  windowSolved: false,
  anchorYaw: 0,
  windMph: 0,
  windDeg: 0,
  swingClubName: DEFAULT_SWING_CLUB,
  swingAttachment: 'yezro-y14',
  swingCaptureRadius: CAPTURE_RADIUS,
  swingAssist: 0.5,  // Golf+-ish default; ?assist=0 for raw measurements
  swingPower: 1.15,  // a compact indoor swing still reaches real distances
  swingDebug: false,
  clubHand: 'right',
  simMode: false,
  immersive: false,
  maskMode: 'punch',
  confirmRequested: false,
  bridgeConnected: false,
  monitorLive: false,
  handedSide: 1,
  perfHud: false,
  frameEmaMs: 13,
  lastPerfCheckAt: 0,
  foveation: 0.4,
  presetName: DEFAULT_PRESET, // re-read from xrControls once they exist
};

const GroundThudSound = '../sounds/thud.wav';

/** Portal distance when no window is active (the historical default). */
const DEFAULT_PORTAL_DIST = 1.2;

/**
 * Wind, in the sim frame the ball flies in (tee origin, +Z downrange):
 * deg 0 = tailwind (blowing downrange), 180 = into the player,
 * 90 = pushing the ball toward the player's RIGHT (−X in the sim frame).
 */
function applyWind() {
  const physics = gameContext.golfBall?.physics;
  if (!physics) return;
  const mps = UnitConversions.milesPerHourToMetersPerSecond(gameContext.windMph);
  const rad = THREE.MathUtils.degToRad(gameContext.windDeg);
  physics.wind.set(-Math.sin(rad) * mps, 0, Math.cos(rad) * mps);
  gameContext.flagSticks?.forEach(stick => setFlagWind(stick));
  gameContext.windAmbience?.setWind(gameContext.windMph);
}

/**
 * Orient a flag downwind and scale its cloth force. The cloth sim blows along
 * the flagstick's LOCAL +Z, so yawing the whole stick points the flutter
 * downwind; rotation.y = −rad maps local +Z onto the wind vector
 * (−sin rad, 0, cos rad).
 */
function setFlagWind(stick: FlagStick) {
  const mps = UnitConversions.milesPerHourToMetersPerSecond(gameContext.windMph);
  stick.object.rotation.y = -THREE.MathUtils.degToRad(gameContext.windDeg);
  stick.windForce.set(0, 0, mps > 0.5 ? 0.35 + mps * 0.28 : 0);
}

/** Which impact voice a monitor shot should use — the bridge doesn't say. */
function voiceForShot(shot: OpenGolfSim.Shot): ImpactVoice {
  if (shot.verticalLaunchAngle < 1) return 'putt';
  if (shot.ballSpeed >= 135) return 'wood';
  if (shot.ballSpeed >= 85) return 'iron';
  return 'wedge';
}

/** Human description of the wind for hints/help ("12 mph, into · L→R"). */
function windLabel(): string {
  if (gameContext.windMph <= 0) return 'calm';
  const deg = ((gameContext.windDeg % 360) + 360) % 360;
  const along = Math.cos(THREE.MathUtils.degToRad(deg));
  const across = -Math.sin(THREE.MathUtils.degToRad(deg));
  const parts: string[] = [];
  if (along > 0.35) parts.push('helping');
  else if (along < -0.35) parts.push('into');
  if (across < -0.35) parts.push('L→R');
  else if (across > 0.35) parts.push('R→L');
  return `${gameContext.windMph} mph ${parts.join(' · ') || 'swirling'}`;
}

/**
 * Frame (or unframe) the virtual world into a window. The window plane doubles
 * as the portal plane, so the two move together; dropping the window restores
 * the classic infinite portal at its default distance.
 */
/** How long the "window framed" announcement stays up, seconds. */
const WINDOW_HINT_SECONDS = 4;

function applyWindow(rect: WindowRect | null, announce = false) {
  const mask = gameContext.worldMask;
  if (!mask) return;
  mask.setWindow(rect);
  mask.uPortalDist.value = rect ? rect.dist : DEFAULT_PORTAL_DIST;
  if (rect && announce) {
    // Self-dismissing: the solve lands once per anchor, so without a hold this
    // line would sit in front of the player for the rest of the session. It
    // reports something already done — nothing is waiting on the player to
    // read it — so long enough to notice and read is long enough.
    gameContext.hint?.setText(
      `Window framed to your room — ${(rect.left + rect.right).toFixed(1)} m wide, ${rect.top.toFixed(1)} m tall`,
      WINDOW_HINT_SECONDS,
    );
  }
}

/** Audible "shot registered" cue — the player is watching the real ball, not
 *  the HUD, when the monitor reports. Plain oscillator: no asset needed. */
function playShotBlip() {
  const ctx = gameContext.audioPlayer?.context;
  if (!ctx || ctx.state !== 'running') return;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.frequency.value = 880;
  gain.gain.setValueAtTime(0.15, ctx.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.12);
  osc.connect(gain).connect(ctx.destination);
  osc.start();
  osc.stop(ctx.currentTime + 0.13);
}

const statusEl = () => document.getElementById('mr-status');

/**
 * Point the chase camera at a tee→aim frame in reference space. Defaults to
 * the sim frame, which is what applies in the desktop preview and after a
 * session ends (worldRoot back at identity).
 */
function updateChaseFrame(tee: THREE.Vector3 = gameContext.startPoint, aim: THREE.Vector3 = gameContext.aimPoint) {
  gameContext.teeWorld.copy(tee);
  gameContext.aimWorld.copy(aim);
  // Establishes shotDirection, which track() needs to sit behind the ball
  gameContext.chaseCamera?.setPositions(gameContext.teeWorld, gameContext.aimWorld);
}

/** Stop driving the TV. Without `hide` the last rendered frame stays up. */
function stopTvChase(options: { hide?: boolean } = {}) {
  gameContext.tvChasing = false;
  gameContext.chaseCamera?.setTracking(false);
  if (options.hide) gameContext.shotTv?.setVisible(false);
}

/** Whether this shot is worth putting on the TV. */
function tvShouldShow(shot: OpenGolfSim.Shot): boolean {
  if (gameContext.tvMode === 'off') return false;
  if (!gameContext.shotTv?.isAvailable || !gameContext.shotTv.isPlaced) return false;
  if (gameContext.tvMode === 'always') return true;
  return (shot.ballSpeed ?? 0) >= TV_MIN_BALL_SPEED_MPH;
}

/**
 * Practice shots are allowed until a real monitor actually delivers a shot —
 * a connected bridge alone doesn't count, because FUSE Link is always
 * connected on the headset even with no monitor attached.
 */
function testShotsAllowed(): boolean {
  return gameContext.testShotsOverride ?? !gameContext.monitorLive;
}

function readyHintText(): string {
  if (isSwingMode()) {
    const swing = gameContext.swingControls;
    if (swing && swing.alignmentState !== 'off') return alignmentHint(swing.alignmentState);
    if (!swing?.isCalibrated) {
      return 'Ready — "Calibrate club" in the wrist menu lines your club up, then swing';
    }
    return `Ready — swing your ${gameContext.swingClubName}`;
  }
  return testShotsAllowed() ? 'Ready — pinch for a practice shot' : 'Ready — hit a real ball';
}

function isSwingMode(): boolean {
  return gameContext.mode === 'swing';
}

/**
 * What belongs in the club slot — the one answer every panel uses.
 *
 * A club in the player's hands beats a practice preset: while you are swinging a
 * real attachment the preset is not what the next shot will be. Everything that
 * paints that slot goes through here, so the wrist panel and the stats panel
 * cannot drift apart.
 */
function clubLabel(): string {
  return isSwingMode() ? gameContext.swingClubName : gameContext.presetName;
}

/** `x,y,z` URL value → a direction, or undefined if absent/unparseable. */
function parseAxis(value: string | null): THREE.Vector3 | undefined {
  if (!value) return undefined;
  const parts = value.split(',').map(part => parseFloat(part));
  if (parts.length !== 3 || parts.some(Number.isNaN)) {
    console.warn(`Ignoring malformed axis '${value}' — expected x,y,z`);
    return undefined;
  }
  const axis = new THREE.Vector3(parts[0], parts[1], parts[2]);
  return axis.lengthSq() > 1e-6 ? axis.normalize() : undefined;
}

/**
 * One source of truth for launch-monitor state, shared by the wrist chip and
 * the side panel so the two can never disagree.
 *
 * The page genuinely cannot tell a monitor that is attached but unswung from no
 * monitor at all: Open Connect heartbeats are answered at the bridge's TCP layer
 * and never reach the browser, so only a real shot proves a monitor is live.
 * Saying "Bridge up · no shots yet" is the honest version of what the side panel
 * used to claim as "Launch monitor connected" — which on a headset running FUSE
 * Link is true even with nothing plugged in.
 */
function launchMonitorState(): WristMenuStatus {
  if (gameContext.monitorLive) return { text: 'Monitor live', tone: 'ok' };
  if (gameContext.bridgeConnected) return { text: 'Bridge up · no shots yet', tone: 'warn' };
  if (gameContext.bridgeEverConnected) return { text: 'Reconnecting…', tone: 'warn' };
  return { text: 'No bridge', tone: 'bad' };
}

/**
 * What the status chip says, per mode. Swing mode has no monitor to report on —
 * what matters there is whether a controller is in hand and whether the club has
 * been calibrated, since an uncalibrated club is the one thing that makes the
 * mode feel broken rather than merely untuned.
 */
function modeStatus(): WristMenuStatus {
  if (!isSwingMode()) return launchMonitorState();
  const swing = gameContext.swingControls;
  if (!swing) return { text: 'Swing mode', tone: 'warn' };
  if (gameContext.renderer?.isPresenting && !swing.hasGrip) {
    return { text: 'Swing · no controller', tone: 'bad' };
  }
  if (!swing.isCalibrated) return { text: 'Swing · calibrate club', tone: 'warn' };
  return { text: `Swing · ${swing.attachment.label}`, tone: 'ok' };
}

/**
 * Push the current status to the side panel. The wrist chip pulls the same
 * helper on each redraw, so only this side needs pushing.
 */
function refreshMonitorStatus() {
  gameContext.statsPanel?.setStatusLabel(modeStatus().text);
}

/** Any pokeable panel is up, so positional input owns the hands. */
function panelOpen(): boolean {
  return !!gameContext.wristMenu?.isOpen
    || !!gameContext.infoPanel?.isOpen
    || !!gameContext.actionMenu?.isOpen;
}

const swingBallWorld = new THREE.Vector3();
const swingTargetDir = new THREE.Vector3();

/**
 * Ball centre in reference space, or undefined when there is nothing to hit.
 * The ball lives under worldRoot; the club is posed in reference space, so the
 * strike test has to compare world positions.
 */
function ballWorldPosition(): THREE.Vector3 | undefined {
  const ball = gameContext.golfBall;
  if (!ball || ball.isShotActive) return undefined;
  if (!gameContext.anchorController?.isAnchored) return undefined;
  return ball.object.getWorldPosition(swingBallWorld);
}

/** Unit horizontal target line in reference space (the anchored aim). */
function targetDirection(): THREE.Vector3 | undefined {
  swingTargetDir.subVectors(gameContext.aimWorld, gameContext.teeWorld).setY(0);
  if (swingTargetDir.lengthSq() < 1e-6) return undefined;
  return swingTargetDir.normalize();
}

/**
 * Switch input worlds. Everything about the launch-monitor path stays where it
 * was; this only moves what the two modes genuinely disagree about — whether the
 * ball is visible at address, whether a club is drawn, and which source is
 * allowed to launch a shot.
 */
function setMode(mode: RangeMode) {
  if (gameContext.mode === mode) return;
  gameContext.mode = mode;
  gameContext.pendingShot = undefined;

  const swing = isSwingMode();
  if (!swing) {
    gameContext.swingControls?.reset();
    gameContext.controllerRays?.reset();
    gameContext.clubOverlay?.setVisible(false);
    gameContext.clubOverlay?.showAlignment(false);
    gameContext.rayTarget = undefined;
    // A ray-driven menu has no wrist to fall back on once rays stop updating
    if (gameContext.wristMenu?.isRayOpen) gameContext.wristMenu.close();
    // The action menu is controller-only for the same reason, and harder to
    // miss: it's large, parked, and there is no longer a ray to dismiss it with
    gameContext.actionMenu?.close();
  } else {
    gameContext.clubOverlay?.showAlignment(!!gameContext.anchorController?.isAnchored);
  }

  // Mode changes hand the input focus around — nothing half-armed or parked
  // may survive the handover
  gameContext.infoPanel?.close();
  gameContext.reanchorArmedUntil = undefined;

  gameContext.statsPanel?.setPresetLabel(swing ? gameContext.swingClubName : gameContext.presetName);
  refreshMonitorStatus();
  if (gameContext.anchorController?.isAnchored) gameContext.hint?.setText(readyHintText());
  // An uncalibrated club is drawn from a guess about grip space, so offer the
  // outline straight away rather than letting the player swing a wrong club
  if (swing && gameContext.renderer?.isPresenting && !gameContext.swingControls?.isCalibrated) {
    beginClubAlignment();
  }
}

/**
 * Automatic mode selection: hands on a real club → launch monitor; the club
 * controller in hand → virtual swing. Policy sits here; the sensing (and the
 * "presence proves nothing" multimodal problem) lives in InputModalityDetector.
 * Runs every presenting frame; setMode early-returns when nothing changes.
 */
function applyAutoMode() {
  if (gameContext.modeForced || !gameContext.modeDetector) return;
  // Never yank the world mid-flight; the recommendation is re-polled next
  // frame, so the switch lands right after shotEnded
  if (gameContext.golfBall?.isShotActive) return;
  // Calibrating means the controller is demonstrably in hand — don't fight it
  if (gameContext.swingControls && gameContext.swingControls.alignmentState !== 'off') return;

  const recommendation = gameContext.modeDetector.recommendation;
  if (!recommendation) return; // no evidence — change nothing
  const mode: RangeMode = recommendation === 'controller' ? 'swing' : 'monitor';
  if (mode === gameContext.mode) return;

  setMode(mode);
  // setMode's own hints (ready text / calibration offer) matter more than the
  // announcement — only speak when nothing more specific took the line
  if (mode === 'swing') {
    if (gameContext.swingControls?.alignmentState === 'off') {
      gameContext.hint?.setText('Controller detected — swing mode');
    }
  } else {
    gameContext.hint?.setText('Controllers idle — launch monitor');
  }
}

/**
 * Start (or restart) club calibration: park an outline in front of the player,
 * who lines their real attachment up inside it and pulls the trigger.
 *
 * This is the flow that doesn't need to know anything up front — not the
 * grip-space axis convention, not where the mount sits on the controller, not
 * even whether the tee has been placed. The outline's pose is known, so once the
 * player's club is in it, the head offset and square-face direction fall out of
 * one press, and the shaft ends up inline with the physical attachment because
 * that is literally where they held it.
 */
/**
 * Close every pointable surface.
 *
 * Any flow that needs the trigger for ITSELF has to do this. An open panel
 * claims the trigger twice over — as a click on whatever the ray is resting on,
 * and via the `panelOpen()` veto that stops a stray pinch becoming a shot — so
 * leaving one up strands the flow with no way to confirm. Calibration hit
 * exactly that: it is reached from a menu row, so the menu that launched it was
 * still up, swallowing the very trigger pull meant to set the club position.
 */
function closePanels() {
  gameContext.infoPanel?.close();
  gameContext.wristMenu?.close();
  gameContext.actionMenu?.close();
}

function beginClubAlignment() {
  const swing = gameContext.swingControls;
  if (!swing) return;
  calibrationRejections = 0;
  // The outline is placed relative to the head, so it must not be parked while
  // a panel is between the player and it — and the trigger it needs must not be
  // going to a menu
  closePanels();
  gameContext.clubGhost?.setClub(swing.club); // outline shows the club being set up
  swing.beginAlignment(getHeadPose(!!gameContext.renderer?.isPresenting));
}

/** Consecutive rejected locks — two in a row re-park the outline. */
let calibrationRejections = 0;

/** Default tee stand-off from the player: 2.5 ft. */
const TEE_FORWARD = 0.762;
/** Adjust-stage step sizes — 1 cm and half a degree per stick detent. */
const TEE_MOVE_STEP = 0.01;
const TEE_AIM_STEP = 0.5;

/**
 * Where the tee goes when there is no real ball to place it against.
 *
 * Assumes the player is standing at address, because that is what they will be
 * doing: the ball 2.5 ft in front of them, and the range running PERPENDICULAR
 * to the way they face. Standing to a golf ball means the target is off to your
 * side, not in front of you — the first cut of this aimed the range down the
 * player's own facing and put the tee at their feet, which stood them ON the tee
 * facing down the range instead of beside it.
 *
 * Which perpendicular depends on the hand: a right-hander's target is 90° to
 * their left, a left-hander's to their right. `left = up × forward`.
 */
function defaultTeePose(): { position: THREE.Vector3; yaw: number } {
  const head = getHeadPose(!!gameContext.renderer?.isPresenting);
  const forward = new THREE.Vector3(0, 0, -1).applyQuaternion(head.quaternion);
  forward.y = 0;
  if (forward.lengthSq() < 1e-6) forward.set(0, 0, -1); // looking straight down
  forward.normalize();

  const target = new THREE.Vector3(forward.z, 0, -forward.x); // up × forward
  if (gameContext.clubHand === 'left') target.negate();

  return {
    // y = 0 is the floor: the session is local-floor, and the tee belongs on the
    // ground however high the player is holding their head
    position: new THREE.Vector3(
      head.position.x + forward.x * TEE_FORWARD,
      0,
      head.position.z + forward.z * TEE_FORWARD,
    ),
    yaw: Math.atan2(target.x, target.z),
  };
}

/** Put the tee at the default address pose (immersive mode has nothing to aim at). */
function placeTeeByDefault() {
  const pose = defaultTeePose();
  gameContext.anchorController?.placeDirect(pose.position, pose.yaw);
}

function teeAdjustHint(): string {
  return gameContext.teeAdjust === 'aim'
    ? 'Stick swings the target line · click the stick to move the tee · trigger when done'
    : 'Stick moves the tee · click the stick to aim the range · trigger when done';
}

/**
 * Let the player position the tee by eye. Only in immersive mode: in passthrough
 * the tee is placed against a real ball, which is a better reference than any
 * amount of nudging.
 */
function beginTeeAdjust() {
  if (!gameContext.immersive || !gameContext.anchorController?.isAnchored) return;
  closePanels(); // the trigger that accepts is the one a panel would swallow
  gameContext.teeAdjust = 'move';
  gameContext.teeStick ??= new StickRepeat();
  gameContext.teeStick.reset();
  gameContext.anchorController.showTeeMarker(true);
  gameContext.hint?.setText(teeAdjustHint());
}

function endTeeAdjust() {
  if (!gameContext.teeAdjust) return;
  gameContext.teeAdjust = undefined;
  gameContext.teeStick?.reset();
  gameContext.anchorController?.showTeeMarker(false);
  // The club-calibration offer that 'anchored' makes in passthrough is deferred
  // to here in immersive mode, so the two flows queue instead of colliding —
  // place the tee, then set up the club against it.
  if (isSwingMode() && !gameContext.swingControls?.isCalibrated) {
    beginClubAlignment();
  } else {
    gameContext.hint?.setText(readyHintText());
  }
}

/** Edge state for the stick click, which switches move ↔ aim. */
let teeStickClickWas = false;

/**
 * Drive the adjust stage from the club-hand controller's stick.
 *
 * Reads the gamepad through `xrControls.inputSources` rather than via
 * XRSwingControls, which only updates in swing mode — the tee needs positioning
 * in launch-monitor mode too, where the player may still be holding a controller
 * even though nothing is being swung.
 */
function updateTeeAdjust(delta: number) {
  const anchor = gameContext.anchorController;
  const stick = gameContext.teeStick;
  if (!gameContext.teeAdjust || !anchor || !stick) return;

  const source = gameContext.xrControls?.inputSources
    .find(candidate => candidate && !(candidate as any).hand && candidate.gamepad);
  const gamepad = source?.gamepad;

  const clicked = !!gamepad?.buttons[3]?.pressed; // xr-standard 3 = stick press
  if (clicked && !teeStickClickWas) {
    gameContext.teeAdjust = gameContext.teeAdjust === 'move' ? 'aim' : 'move';
    stick.reset(); // a live repeat would step the new axis the instant it's picked
    gameContext.hint?.setText(teeAdjustHint());
  }
  teeStickClickWas = clicked;

  const steps = stick.sample(delta, gamepad?.axes);
  if (!steps.x && !steps.y) return;

  if (gameContext.teeAdjust === 'aim') {
    anchor.nudgeAim(steps.x * TEE_AIM_STEP);
    return;
  }

  // Moves in the PLAYER's frame, not the world's: "push the stick away" has to
  // send the tee away from where they are standing, whichever way they've turned
  // since it was placed. Stick up is negative Y, hence the negation.
  const head = getHeadPose(true);
  const forward = new THREE.Vector3(0, 0, -1).applyQuaternion(head.quaternion);
  forward.y = 0;
  if (forward.lengthSq() < 1e-6) forward.set(0, 0, -1);
  forward.normalize();
  const right = new THREE.Vector3(-forward.z, 0, forward.x);
  const move = new THREE.Vector3()
    .addScaledVector(forward, -steps.y * TEE_MOVE_STEP)
    .addScaledVector(right, steps.x * TEE_MOVE_STEP);
  anchor.nudgeTee(move.x, move.z);
}

/**
 * Trigger handling while calibrating: one pull locks AND saves, then flows into
 * the optional fine-tune stage; the next pull accepts the trims. Rejections
 * explain themselves, and two in a row mean the outline is probably parked
 * somewhere awkward — re-park it where the player NOW stands.
 */
function advanceClubAlignment(): boolean {
  const swing = gameContext.swingControls;
  if (!swing) return false;
  if (swing.alignmentState === 'tuning') return swing.acceptTuning();
  if (swing.alignmentState !== 'aligning') return false;

  const result = swing.lockAlignment();
  switch (result) {
    case 'ok':
      // Hint comes from the 'calibrated' handler
      calibrationRejections = 0;
      break;
    case 'no-grip':
      // Nothing mis-aimed — the controller just isn't in hand. Doesn't count
      // toward the re-park.
      gameContext.hint?.setText('Pick up your controller, then pull the trigger');
      break;
    case 'moving':
    case 'too-far': {
      calibrationRejections++;
      if (calibrationRejections >= 2) {
        // The outline is probably behind them or out of reach — bring it back
        swing.cancelAlignment();
        beginClubAlignment();
        gameContext.hint?.setText('Outline re-placed — take your address over it and pull the trigger');
      } else {
        gameContext.hint?.setText(result === 'moving'
          ? 'Hold still at address for a moment, then pull the trigger'
          : 'Not quite — put the whole club inside the outline and pull again');
      }
      break;
    }
  }
  return true;
}

/**
 * A/X during fine-tuning: sole the club at the ball to snap the length exactly.
 * The snap rebases the trims, so the stick continues from the snapped geometry.
 */
function calibrateFromBall() {
  const swing = gameContext.swingControls;
  const ball = ballWorldPosition();
  const target = targetDirection();
  if (!swing) return;
  if (!ball || !target) {
    gameContext.hint?.setText('Place the tee first to snap to the ball');
    return;
  }
  if (swing.calibrate(ball, target)) {
    gameContext.hint?.setText('Snapped to the ball — stick to fine-tune, trigger when done');
  } else {
    gameContext.hint?.setText('Missed — sole the club head right at the ball and press again');
  }
}

/** Hint copy for wherever calibration has got to. */
function alignmentHint(state: 'off' | 'aligning' | 'tuning'): string {
  if (state === 'aligning') {
    return 'Address the outline on the floor — hands on its grip, shaft along your club — then pull the trigger';
  }
  if (state === 'tuning') {
    const tuning = gameContext.swingControls?.tuning;
    if (!tuning) return readyHintText();
    const cm = (metres: number) => `${metres >= 0 ? '+' : ''}${(metres * 100).toFixed(1)} cm`;
    const deg = (degrees: number) => `${degrees >= 0 ? '+' : ''}${degrees.toFixed(1)}°`;
    // Only the live pair is read out. All six trims at once is unreadable at
    // arm's length in passthrough, and the other four are still whatever the
    // player last set them to — visible on the club itself.
    const pairs: Record<typeof tuning.axis, string> = {
      length: `Slide ${cm(tuning.shift)} · face ${deg(tuning.twist)}`,
      offset: `Across ${cm(tuning.offsetFace)} · along ${cm(tuning.offsetSide)}`,
      tilt: `Lie ${deg(tuning.pitch)} · aim ${deg(tuning.yaw)}`,
    };
    const names: Record<typeof tuning.axis, string> = {
      length: 'length & face',
      offset: 'position',
      tilt: 'tilt',
    };
    return `${pairs[tuning.axis]} · stick adjusts ${names[tuning.axis]}`
      + ' · click stick for next · A/X snaps to ball · trigger done';
  }
  return readyHintText();
}

/** An impact — real or synthetic — becomes exactly the shot a monitor would send. */
function launchSwing(impact: Impact, club: ClubSpec) {
  gameContext.lastImpactVoice = club.head; // the strike sound knows the club
  const swing = gameContext.swingControls;
  const { shot, derived } = deriveShot(impact, club, {
    // Same radius the detector used, so strike quality is judged on one scale
    captureRadius: swing?.effectiveCaptureRadius ?? gameContext.swingCaptureRadius,
    handSign: swing?.handSign ?? 1,
    assist: gameContext.swingAssist,
    power: gameContext.swingPower,
    shape: gameContext.swingShape,
    clubGain: gameContext.swingClubGain,
  });
  if (gameContext.swingDebug) {
    console.log(`swing → ${club.name}`, { shot, derived });
  }
  launchShot(shot);
}

/**
 * Desktop-only stand-in for a swing: there is no controller to swing in the
 * preview, and the model is the part most worth checking without a headset.
 */
function fireSyntheticSwing() {
  const club = gameContext.swingControls?.club ?? VIRTUAL_CLUBS[0];
  launchSwing(syntheticImpact(club), club);
}

/** Help panel copy. Rebuilt on each redraw so the status line stays live. */
function helpPages(): InfoPage[] {
  return [
    {
      title: 'Launch monitor setup',
      body: [
        `Status:  ${launchMonitorState().text}`,
        `Wind:  ${windLabel()}  (?wind=mph,deg — 0° tail · 180° into · 90° L→R)`,
        `Bridge:  ${gameContext.bridgeUrl}`
          + (gameContext.bridgeUrl === DEFAULT_BRIDGE_URL && window.location.hostname !== 'localhost'
            ? '  ⚠ loopback default — on a hosted page, add ?bridge=wss://<pi-ip>:8443/ws'
            : ''),
        '',
        '• Run FUSE Link on the headset and tap Start — it shows your LAN IP.',
        '• Point your connector at <headset-ip> port 3111 (OpenFlight/OGS) or 921 (GSPro).',
        '• Open http://localhost:9210/range-mr/ in the Quest browser, then Enter MR.',
        '',
        'PC bridge: npm run dev:mr, accept both certificates, then add ?bridge=wss://<pc-ip>:8443/ws',
        '',
        'The range shows through a window framed by your walls and ceiling (needs'
          + ' Space Setup). ?window=off for the infinite view, ?window=dist,left,right,top'
          + ' to size it by hand.',
        '',
        'Passthrough feels laggy? Bright, even lighting is the biggest fix (dim rooms'
          + ' lengthen camera exposure). Then keep frames held: ?hz=90 pins the refresh'
          + ' rate, ?fbscale=0.85 buys GPU headroom, ?perf=1 shows frame times.',
      ],
    },
    {
      title: 'Controls',
      body: [
        '• Look at your ball and pinch to place the tee. Look downrange and pinch again to aim.',
        '• Pinch to fire a practice shot. Thumb to middle finger cycles the club.',
        '• Hold a pinch two seconds to re-place the tee.',
        '• Holding a controller instead? Its trigger does everything a pinch does; hold'
          + ' the trigger two seconds (or the grip button) to re-place the tee.',
        '• Palm toward you plus a glance opens this menu; poke with your other index finger.',
        '',
        'Hand input pauses while you grip and swing, so a swing never fires anything.',
        '',
        'Practice pinches lock out once a monitor sends a real shot (?testshots=1 keeps them).',
      ],
    },
    {
      title: 'Virtual golf (swing mode)',
      body: [
        `Mode:  ${isSwingMode() ? 'Swing — hitting a virtual ball' : 'Launch monitor — hitting a real ball'}`,
        `Grip:  ${gameContext.swingControls?.attachment.label ?? '—'}`
          + `${gameContext.swingControls?.isCalibrated ? ' (calibrated)' : ' (not calibrated)'}`,
        '',
        'The mode follows your input device: pick up your club controller to swing a'
          + ' virtual ball; set it down and use bare hands to hit real balls off the'
          + ' monitor. A YEZRO Y14 (or any weighted handle) on the right controller'
          + ' gives it the weight and length of a real club. (?mode= forces one mode.)',
        '',
        '• Calibrate — a club outline stands on the floor ahead of you, soled at'
          + ' address. Take your address over it: hands on its grip, shaft along your'
          + ' attachment, hold still a moment, pull the trigger. That saves instantly'
          + ' (pose averaged over a quarter second), then an optional fine-tune stage'
          + ' opens. The stick refines the club in all six axes — three positions'
          + ' and three rotations — two at a time, and CLICKING THE STICK cycles'
          + ' which two: length & face (slide along the attachment, twist the face),'
          + ' then position (move it across the handle), then tilt (lie and aim, for'
          + ' a mount clamped at an angle). Taps are fine steps, holds accelerate,'
          + ' and A/X snaps the head to the ball once the tee is placed. Trigger'
          + ' finishes; B/Y reverts the trims; holding the trigger redoes the outline.'
          + ' The outline follows you if you walk away from it.',
        '• Swing to hit. Head speed, attack angle, club path, face angle and where on'
          + ' the face you caught it all shape the shot, Golf+ style. Face vs path is'
          + ' the curve: hold the face open through impact for a fade, closed for a'
          + ' draw; swing out-to-in with an open face and it slices. Longer clubs'
          + ' reach further — the head follows the selected club\'s real length.',
        '• A/X cycles the club, B/Y opens the menu — then point at it with either'
          + ' controller and pull the trigger to click.',
        '',
        'Calibration is remembered per attachment. ?attach=none|yezro|<metres>,'
          + ' ?swingzone=<metres> for strike forgiveness, ?assist=0..1 for shot shaping'
          + ' (default 0.5; 0 = raw physics), ?power=0.5..1.6 for distance (default 1.15),'
          + ' ?shape=0..3 for curve response (default 1 = true D-plane), ?swingdebug=1.',
      ],
    },
  ];
}

/**
 * Rapid-fire range flow: a real launch-monitor shot cuts the in-flight sim
 * short (the player has already hit another ball — Golf+/GSPro behavior);
 * practice shots fired while one is in flight are queued instead of dropped.
 */
function launchShot(shot: OpenGolfSim.Shot, source: 'bridge' | 'local' = 'local') {
  if (!shot.ballSpeed) return;
  // While presenting, shots only make sense once the world is on the ball
  if (gameContext.renderer?.isPresenting && !gameContext.anchorController?.isAnchored) return;
  const ball = gameContext.golfBall;
  if (!ball) return;

  if (ball.isShotActive) {
    if (source === 'bridge') {
      ball.abortShot(); // emits shotEnded → setupNextShot resets synchronously
    } else {
      gameContext.pendingShot = shot;
      gameContext.hint?.setText('Shot queued');
      return;
    }
  }
  ball.launchShot(shot);
  // The strike itself: per-club synthesized crack, scaled by ball speed.
  // Swing mode set the voice from the actual club; monitor shots infer it.
  const voice = gameContext.lastImpactVoice ?? voiceForShot(shot);
  gameContext.lastImpactVoice = undefined;
  if (gameContext.impactSound) gameContext.impactSound.play(voice, shot.ballSpeed);
  else playShotBlip();

  // Chase-cam TV. The gate has to be a launch-time estimate from ball speed:
  // the physics integrates forward with no lookahead, so the real carry isn't
  // known until the ball is most of the way there.
  if (gameContext.shotTv) {
    const show = tvShouldShow(shot);
    gameContext.shotTv.setVisible(show);
    gameContext.tvChasing = show;
    gameContext.tvFrame = 0;
    if (show) {
      gameContext.chaseCamera?.setTracking(true, Math.min(shot.ballSpeed / 150, 1));
    } else {
      gameContext.chaseCamera?.setTracking(false);
    }
  }
}

async function setupWorld() {
  gameContext.timer.connect(document);

  const canvas = document.getElementById('canvas');
  if (!canvas || !(canvas instanceof HTMLCanvasElement)) throw new Error('Unable to find canvas in HTML.');

  gameContext.renderer = new FuseRenderer({
    canvas,
    adaptive: false,
    renderMode: 'webgpu',
    xr: true,
    qualityLevel: gameContext.setupData?.qualityLevel ?? 0, // Low: direct render, no pipeline
  });

  await gameContext.renderer.init();
}

async function createGroundPlane() {
  if (!gameContext.scene || !gameContext.worldRoot) throw new Error('Missing base scene');
  const rangeWidth = 500;
  const rangeHeight = 700;
  const widthRatio = rangeHeight / rangeWidth;
  const grassScale = 100;

  const textureLoader = new THREE.TextureLoader();
  const grassTexture = textureLoader.load(fairwayTexture);
  grassTexture.wrapS = THREE.RepeatWrapping;
  grassTexture.wrapT = THREE.RepeatWrapping;
  grassTexture.repeat.set(grassScale, grassScale * widthRatio);
  grassTexture.colorSpace = THREE.SRGBColorSpace;
  grassTexture.anisotropy = gameContext.renderer?.getMaxAnisotropy() || 1;

  const grassNormalMap = textureLoader.load(fairwayMap);
  grassNormalMap.wrapS = THREE.RepeatWrapping;
  grassNormalMap.wrapT = THREE.RepeatWrapping;
  grassNormalMap.repeat.set(grassScale, grassScale * widthRatio);
  grassNormalMap.colorSpace = THREE.SRGBColorSpace;
  grassNormalMap.anisotropy = gameContext.renderer?.getMaxAnisotropy() || 1;

  const floorMaterial = new THREE.MeshStandardMaterial({
    name: 'floor',
    map: grassTexture,
    normalMap: grassNormalMap,
    roughness: 1,
    metalness: 0,
  });

  const floorGeometry = new THREE.PlaneGeometry(rangeWidth, rangeHeight, 50, 70);

  gameContext.ground = new THREE.Mesh(floorGeometry, floorMaterial);
  gameContext.ground.rotation.x = -Math.PI / 2;
  gameContext.ground.position.y = 0;
  gameContext.ground.position.z = 140;
  gameContext.ground.userData.surface = 'fairway';
  gameContext.worldRoot.add(gameContext.ground);

  // The yardage-lines clone (opaque, carries the same fairway map) fully
  // covers the base plane, which exists for physics only in this app.
  gameContext.ground.visible = false;

  gameContext.groundLines = gameContext.ground.clone();
  gameContext.groundLines.position.y = 0.001;
  gameContext.groundLines.visible = true;
  gameContext.worldRoot.add(gameContext.groundLines);

  const downrange = new THREE.Vector3(0, 0, 1);
  const distances = [...hashMarks].map(val => gameContext.setupData?.units === 'imperial' ? UnitConversions.yardsToMeters(val) : val);

  gameContext.yardageLines = new YardageLinesMaterial(
    gameContext.groundLines,
    gameContext.startPoint,
    downrange,
    distances,
    {
      lineWidth: 0.5,
      lineLength: 50,
      maxTextureSize: gameContext.renderer?.getMaxTextureSize(),
      labels: hashMarks,
      lineColor: [1, 1, 1, 0.5],
      feather: 0.2,
      texelsPerMeter: 40,
    }
  );

  await loadMountain(grassTexture, grassNormalMap);
}

async function loadMountain(grassTexture: THREE.Texture, grassNormalMap: THREE.Texture) {
  gameContext.mountain = await gameContext.meshLoader?.load(rangeMtnsModel, true);
  if (!gameContext.mountain) {
    console.warn('Unable to load mountain mesh!');
    return;
  }

  // Node material, not THREE.MeshStandardMaterial: WorldMask.applyToMaterial
  // requires isNodeMaterial, which the classic classes don't set — so the
  // mountain mask below was being silently skipped with a console warning.
  // (The ground escapes this only because YardageLinesMaterial replaces its
  // material with a MeshStandardNodeMaterial of its own.)
  const mountainMaterial = new MeshStandardNodeMaterial({
    map: grassTexture,
    normalMap: grassNormalMap,
    roughness: 1,
    color: mountainColor,
    normalScale: new THREE.Vector2(0, 0.5),
    metalness: 0,
  });

  // @ts-expect-error single-mesh GLB
  gameContext.mountain.material = mountainMaterial;
  gameContext.mountain.position.set(0, -12, 900);
  gameContext.mountain.scale.set(20, 20, 20);
  gameContext.worldRoot?.add(gameContext.mountain);
}

async function setupRange() {
  await setupWorld();
  const player = gameContext.setupData?.players?.[0];
  if (!player) throw new Error('No player found in setup data');
  gameContext.currentPlayer = new CoursePlayer(player);

  if (!gameContext.renderer) throw new Error('Renderer must be created first');
  gameContext.meshLoader = new MeshLoader(gameContext.renderer);

  gameContext.scene = new THREE.Scene();
  // Passthrough compositing: no sky, no fog — alpha-0 pixels show the room
  gameContext.scene.background = null;
  gameContext.scene.fog = null;

  gameContext.lightGroup = new CourseLight({
    color: sunColor,
    ambient: { enabled: true, intensity: 1.0 },
    directional: { enabled: true, intensity: 1.1 },
  });
  gameContext.scene.add(gameContext.lightGroup);
  gameContext.renderer.renderer.shadowMap.enabled = false;

  // Everything virtual parents under worldRoot, built at identity so the
  // physics BVH bakes sim coordinates (tee at origin, downrange +Z). The
  // anchor drives only this matrix; physics never re-learns the world.
  gameContext.worldRoot = new THREE.Group();
  gameContext.worldRoot.name = 'worldRoot';
  gameContext.worldRoot.matrixAutoUpdate = false;
  gameContext.scene.add(gameContext.worldRoot);

  await createGroundPlane();

  const canvas = document.getElementById('canvas') as HTMLCanvasElement;
  gameContext.camera = new THREE.PerspectiveCamera(
    70,
    (canvas.clientWidth || window.innerWidth) / (canvas.clientHeight || window.innerHeight),
    0.05,
    1000
  );
  // Desktop preview pose — the headset overwrites this with the XR pose
  gameContext.camera.position.set(0, 1.7, -3);
  gameContext.camera.lookAt(0, 1, 30);
  gameContext.scene.add(gameContext.camera);
  window.addEventListener('resize', () => {
    if (!gameContext.camera || gameContext.renderer?.isPresenting) return;
    gameContext.camera.aspect = window.innerWidth / window.innerHeight;
    gameContext.camera.updateProjectionMatrix();
  });

  if (!gameContext.setupData) throw new Error('Missing setupData');
  if (!gameContext.ground) throw new Error('Ground should exist');
  gameContext.golfBall = new GolfBall(gameContext.worldRoot, {
    setupData: gameContext.setupData,
    clearTrail: 'start',
    groundMeshes: [gameContext.ground],
    waitTime: 400,             // range flow: ~no dead time between shots
    trailOptions: {
      layer: 0,                // layers 1/2 are per-eye in XR
      cameraFadeNear: 0.25,    // the head stands at the tee — keep launch visible
      cameraFadeFar: 0.6,      //   (a chip's whole arc is only ~9 m long)
      lineWidth: 0.045,        // survives bright passthrough at 100+ m
      color: 0xff7733,
      qualityLevel: 0,
      sampleEvery: 2,          // Low would sample every 10th frame: ~7 points per chip
      fadeLength: 0.6,         // 2 m of end-fade would eat a quarter of a chip arc
      // Mandatory in XR: the cameraPosition built-in throws under three r185's
      // ArrayCamera, which silently drops MeshLine's vertexNode and collapses
      // the whole ribbon to zero-area triangles. See BallTrailOptions.viewerFade.
      viewerFade: 'uniform',
    },
  });
  gameContext.golfBall.on('shotEnded', onShotEnded);

  gameContext.contactShadow = new ContactShadow();
  gameContext.worldRoot.add(gameContext.contactShadow.object);

  // Audio: the player is watching the real ball, not the HUD — sound is the
  // only channel that reliably reaches them at address
  gameContext.audioPlayer = new AudioPlayer();
  gameContext.impactSound = new ImpactSound(gameContext.audioPlayer);
  gameContext.windAmbience = new WindAmbience(gameContext.audioPlayer);
  gameContext.audioPlayer.load(GroundThudSound).catch(() => console.warn('Could not load thud sound'));
  const landingPos = new THREE.Vector3();
  gameContext.golfBall.on('landed', velocity => {
    // Spatialized: a 200-yard drive should LAND 200 yards out, not in the ear
    gameContext.golfBall!.object.getWorldPosition(landingPos);
    gameContext.audioPlayer?.playAt(GroundThudSound, landingPos, Math.min(1, velocity / 30));
  });

  // Trail must survive bright, denoised passthrough: wider, brighter, and
  // excluded from tone mapping so exposure 1.0 doesn't dull it
  gameContext.golfBall.ensureTrail();
  const trailMaterial: any = gameContext.golfBall.trail?.line.material;
  if (trailMaterial && !Array.isArray(trailMaterial)) trailMaterial.toneMapped = false;

  // Golf+-style portal: virtual world only beyond the plane downrange of the
  // ball, passthrough floor bay around the mat.
  //
  // Skipped entirely in immersive mode. The mask exists to protect the room
  // from being painted over, and in VR there is no room to protect — every
  // consumer of `worldMask` is optional-chained, so leaving it undefined is the
  // whole switch.
  if (!gameContext.immersive) {
  gameContext.worldMask = new WorldMask({ featherMode: gameContext.maskMode });
  if (gameContext.groundLines) {
    gameContext.worldMask.applyToMaterial(gameContext.groundLines.material as THREE.Material, { ground: true });
  }
  // Mountains: the portal term is ≡1 at 900 m, but masking them keeps the
  // optional side-wall/window limits effective if enabled during tuning
  const mountainMaterial = (gameContext.mountain as THREE.Mesh | undefined)?.material;
  if (mountainMaterial && !Array.isArray(mountainMaterial)) {
    gameContext.worldMask.applyToMaterial(mountainMaterial, { ground: false });
  }
  }

  // Sky as a masked dome rather than scene.background / backgroundNode: a
  // screen-space background paints every pixel, which in passthrough erases
  // the real room everywhere, portal included. Running the dome through the
  // same mask makes the sky end at the MR boundary — present downrange,
  // absent behind the player and directly overhead, where the real ceiling
  // should still show through.
  // No sunDirection override: the dome computes its gradient from local
  // position, so the sun lives in worldRoot/sim space (+Z downrange) and its
  // default is up-and-downrange. Feeding it the CourseLight direction would be
  // wrong — that light sits in reference space, so the sun would slide sideways
  // every time the tee is re-anchored.
  // In immersive mode the dome is simply the sky: unmasked, it closes over the
  // whole view, which is exactly what a screen-space background would have done
  // and what passthrough could never allow.
  gameContext.skyDome = new SkyDome({ sunColor });
  gameContext.worldRoot.add(gameContext.skyDome.object);
  gameContext.worldMask?.applyToMaterial(gameContext.skyDome.material, { ground: false });

  // Range targets: greens with flags at real distances down the aim line, so
  // there is somewhere to AIM instead of an empty fairway. Built in sim
  // coordinates under worldRoot (tee origin, +Z downrange), masked like the
  // rest of the virtual world so the window/portal clips them correctly.
  // No golf cup: its shader uses the cameraPosition built-in, which is broken
  // in r185 XR (see BallTrailOptions.viewerFade) — and a cup is invisible at
  // 50+ yards anyway.
  {
    const targets = new THREE.Group();
    targets.name = 'RangeTargets';
    gameContext.flagSticks = [];
    for (const yards of [50, 100, 150, 200]) {
      const z = UnitConversions.yardsToMeters(yards);
      const greenMaterial = new MeshStandardNodeMaterial({
        color: 0x2f7a45, // a step darker than the fairway so the green reads
        roughness: 0.85,
        metalness: 0,
      });
      const green = new THREE.Mesh(
        new THREE.CircleGeometry(4 + yards * 0.02, 40).rotateX(-Math.PI / 2),
        greenMaterial
      );
      green.position.set(0, 0.006, z); // above the yardage-lines clone
      green.renderOrder = 1;
      targets.add(green);
      gameContext.worldMask?.applyToMaterial(greenMaterial, { ground: true });

      const stick = new FlagStick(new THREE.Vector3(0, 0, z), `${yards}`);
      // FlagStick builds node materials itself now, so these only need masking —
      // the swap that used to live here is gone.
      gameContext.worldMask?.applyToMaterial(stick.flag.material as THREE.Material, { ground: false });
      gameContext.worldMask?.applyToMaterial(stick.stick.material as THREE.Material, { ground: false });
      setFlagWind(stick);
      gameContext.flagSticks.push(stick);
      targets.add(stick.object);
    }
    gameContext.worldRoot.add(targets);
  }

  // In-world UI, three layers: ground stat tiles downrange (the main HUD),
  // the upright side panel for preset/player info, and a head-following hint
  // line for instructions that must be readable wherever the player looks
  gameContext.groundHud = new GroundStatsHUD({ units: gameContext.setupData.units });
  gameContext.scene.add(gameContext.groundHud.object);
  gameContext.statsPanel = new ShotStatsPanel({ units: gameContext.setupData.units });
  gameContext.scene.add(gameContext.statsPanel.object);
  gameContext.hint = new HeadLockedHint();
  gameContext.scene.add(gameContext.hint.object);

  // Chase-cam TV: across the tee at eye level, so it is never in the ball path
  // or the downrange view. Hidden until a shot qualifies.
  gameContext.shotTv = new ShotTv();
  gameContext.scene.add(gameContext.shotTv.object);
  // Detached canvas purely so ShotPerspectiveCamera's own resize handling
  // derives the render target's 16:9 aspect rather than the window's.
  const tvCanvas = document.createElement('canvas');
  tvCanvas.width = 640;
  tvCanvas.height = 360;
  // Deliberately NOT parented to worldRoot: Object3D.lookAt treats its target
  // as a world position, so a camera positioned in worldRoot-local space would
  // aim at the wrong point once the anchor transform is non-identity. Left
  // unparented and fed world-space poses instead.
  gameContext.chaseCamera = new ShotPerspectiveCamera({
    canvas: tvCanvas,
    fov: 30,
    near: 0.5,
    far: 1000,
    // Low and close. Height first: the desktop default ([6, 25]) looks down at
    // the ball hard enough that the picture is all turf and no sky. Distance
    // second: the trail is 0.045 m wide with sizeAttenuation, so much past
    // ~20 m it falls below one pixel on a 640x360 target and washes out — the
    // tracer, not the 4 cm ball, is what makes the shot readable here.
    cameraTrackingOffsetYZ: [1.5, 18],
    // The desktop default is 3 s of static wide shot before the chase begins,
    // which on a 1 m screen throws away the launch. 500 is the floor the
    // camera's own setTracking() clamps to.
    trackingDelay: 500,
  });

  // Setup / help panel, opened from the wrist menu. Content is a callback so
  // the status line and bridge URL are current every time it redraws.
  gameContext.infoPanel = new InfoPanel({ pages: helpPages });
  gameContext.scene.add(gameContext.infoPanel.object);
  gameContext.infoPanel.on('openChanged', open => {
    // The hint line sits 1.4 m ahead with depthTest off — it would punch
    // straight through the panel. Same mute/restore idiom used at session end.
    gameContext.hint?.setText(open ? '' : (gameContext.anchorController?.isAnchored ? readyHintText() : ''));
    // A ray-driven wrist menu parks closer than the help panel, so leaving both
    // up would put the pointer dot on the menu while the hover reads the panel
    // behind it. One pointable surface at a time.
    if (open && gameContext.wristMenu?.isRayOpen) gameContext.wristMenu.close();
    if (open && gameContext.actionMenu?.isOpen) gameContext.actionMenu.close();
  });

  // Two of the eight slots do double duty rather than growing the panel: the
  // button grid's height is baked at construction, and a taller panel on the
  // wrist is harder to poke, not easier.
  gameContext.wristMenu = new WristMenu({
    clubLabel: () => clubLabel(),
    status: modeStatus,
    palmOffset: gameContext.wristPalmOffset,
    distalOffset: gameContext.wristDistalOffset,
    buttons: [
      // No mode button: the mode follows the input device automatically —
      // pick up the club controller to swing, use bare hands for the monitor
      { id: 'action', label: () => isSwingMode() ? 'Calibrate club' : 'Practice shot' },
      { id: 'reanchor', label: () => 'Re-place tee' },
      { id: 'units', label: () => `Units: ${gameContext.setupData?.units === 'metric' ? 'M' : 'YD'}` },
      { id: 'tv', label: () => `TV: ${gameContext.tvMode === 'off' ? 'off' : 'on'}` },
      {
        id: 'aux',
        label: () => isSwingMode()
          ? `Grip: ${gameContext.swingControls?.attachment.label ?? '—'}`
          : `Perf: ${gameContext.perfHud ? 'on' : 'off'}`,
      },
      { id: 'help', label: () => 'Setup / help' },
      // Takes the reload slot: reload is still on the controller menu's rail,
      // and without this the course is reachable only by typing a URL into a
      // headset. The grid's height is baked at construction, so it is a swap.
      { id: 'playcourse', label: () => 'Play a course' },
      { id: 'exit', label: () => gameContext.immersive ? 'Exit VR' : 'Exit MR' },
    ],
  });
  gameContext.scene.add(gameContext.wristMenu.object);
  gameContext.wristMenu.on('select', onWristMenuSelect);

  // The controller-mode menu. Same actions, same handler, but with a controller
  // in hand there is no wrist to wear a panel and no free finger to poke it, so
  // none of the constraints that shaped the grid above apply — the options can
  // be spread over sections and labelled properly instead of doubling up.
  gameContext.actionMenu = new ActionMenu({
    title: 'Range',
    status: modeStatus,
    // Session-wide, so they live on the rail rather than inside a section —
    // the way out must never be behind the wrong tab
    rail: [
      { id: 'help', label: () => 'Help' },
      { id: 'reload', label: () => 'Reload' },
      { id: 'exit', label: () => gameContext.immersive ? 'Exit VR' : 'Exit MR' },
    ],
    sections: [
      {
        id: 'play',
        title: 'Play',
        items: [
          { id: 'action', label: () => isSwingMode() ? 'Calibrate club' : 'Practice shot' },
          {
            id: 'teeadjust',
            label: () => 'Position tee',
            // Passthrough places the tee against the real ball, which beats any
            // amount of nudging — so this is immersive-only
            disabled: () => !gameContext.immersive,
          },
          {
            id: 'club-next',
            prevId: 'club-prev',
            label: () => isSwingMode() ? 'Club' : 'Shot preset',
            value: () => isSwingMode() ? gameContext.swingClubName : gameContext.presetName,
          },
          { id: 'reanchor', label: () => 'Re-place tee' },
          { id: 'playcourse', label: () => 'Play a course', value: () => 'Mountain Vista' },
        ],
      },
      {
        id: 'setup',
        title: 'Setup',
        items: [
          {
            id: 'grip',
            label: () => 'Grip attachment',
            value: () => gameContext.swingControls?.attachment.label ?? '—',
            // Meaningless without a club in hand, and the wrist menu had to hide
            // this behind a shared slot for want of room
            disabled: () => !isSwingMode(),
          },
          {
            id: 'units',
            label: () => 'Units',
            value: () => gameContext.setupData?.units === 'metric' ? 'Metric' : 'Yards',
          },
          {
            id: 'sync',
            label: () => 'Calibration profile',
            // Shows the code itself so it can be read off in-headset and typed
            // into another device's URL — that is the whole mechanism for
            // carrying a calibration between origins and devices
            value: () => {
              const store = gameContext.calibrationStore;
              if (!store?.isRemote) return 'local only';
              const mark = store.state === 'error' ? ' (offline)'
                : store.state === 'syncing' ? ' …' : '';
              return `${store.profile}${mark}`;
            },
          },
        ],
      },
      {
        id: 'display',
        title: 'Display',
        items: [
          { id: 'tv', label: () => 'Shot TV', value: () => gameContext.tvMode === 'off' ? 'Off' : 'On' },
          { id: 'perf', label: () => 'Frame timing', value: () => gameContext.perfHud ? 'On' : 'Off' },
          // Phrased as an action, not a toggle, because it is one: a session's
          // mode is fixed when it's requested, so this ends the session and
          // comes back on the other flag. See the 'vrmode' case.
          {
            id: 'vrmode',
            label: () => gameContext.immersive ? 'Switch to passthrough' : 'Switch to full VR',
            value: () => 'restarts',
          },
        ],
      },
    ],
  });
  gameContext.scene.add(gameContext.actionMenu.object);
  gameContext.actionMenu.on('select', onWristMenuSelect);

  setupXR();
  setupInput();

  // Label the panel from the preset actually selected — setupXR is what builds
  // xrControls, so this can't move up with the rest of the panel setup.
  gameContext.presetName = gameContext.xrControls?.currentPreset.name ?? DEFAULT_PRESET;
  gameContext.statsPanel.setPresetLabel(clubLabel());
  // Place the TV at the sim origin up front so the desktop preview can show it;
  // the anchor handler re-places it on the real tee. It stays hidden either way
  // until a shot qualifies, and shots are blocked until anchored in MR.
  gameContext.shotTv?.placeAtTee(gameContext.startPoint, 0, gameContext.handedSide);
  updateChaseFrame();

  setupNextShot();
  await gameContext.renderer.compile(gameContext.scene, gameContext.camera);
}

function setupXR() {
  const renderer = gameContext.renderer!;
  const scene = gameContext.scene!;

  // Compositor-latency knobs: passthrough shares the compositor with the app
  // layer, so a HELD refresh rate and GPU headroom are what keep the camera
  // feed feeling immediate. ?hz= picks the closest supported refresh rate;
  // ?fbscale= trades render resolution for headroom (0.8 ≈ 36% fewer pixels).
  const params = new URLSearchParams(window.location.search);
  const hzParam = parseFloat(params.get('hz') ?? '');
  const fbParam = parseFloat(params.get('fbscale') ?? '');
  gameContext.xrSession = new FuseXRSession({
    renderer,
    mode: gameContext.immersive ? 'immersive-vr' : 'immersive-ar',
    targetFrameRate: Number.isFinite(hzParam) ? hzParam : undefined,
    framebufferScale: Number.isFinite(fbParam)
      ? THREE.MathUtils.clamp(fbParam, 0.5, 1.2)
      : undefined,
  });
  gameContext.anchorController = new AnchorController(scene, gameContext.worldRoot!);
  gameContext.xrControls = new XRShotControls(renderer, {
    // A shot in flight suppresses all hand gestures via the swing guard
    isBusy: () => !!gameContext.golfBall?.isShotActive,
    initialPreset: DEFAULT_PRESET,
    // Reaching across to poke a hand-mounted panel brings the wrists as close
    // together as an address grip does (poker fingertip ~6 cm off the wearer's
    // wrist, its own wrist ~18 cm behind that — inside TWO_HAND_GRIP_DIST).
    // Without this the guard suppresses both hands and the menu closes as you
    // reach for it. Safe because pinch-to-fire is already blocked while a panel
    // is open, so the guard's real job is untouched.
    ignoreTwoHandGrip: panelOpen,
    // Swing mode claims A/X (club) and B/Y (menu); preset cycling would fight it
    ignoreControllerButtons: isSwingMode,
    // Until the tee is placed, a confirm can only anchor — it can't launch a
    // ball — so the guard's posture terms have nothing to protect and cost the
    // player every pinch made while holding their club. Motion is still guarded.
    relaxed: () => !gameContext.anchorController?.isAnchored,
  });
  gameContext.modeDetector = new InputModalityDetector({ clubHand: gameContext.clubHand });
  gameContext.roomBounds = new RoomBounds();

  setupSwingMode(scene);

  gameContext.anchorController.on('stateChanged', state => {
    const messages: Record<string, string> = {
      'placing-ball': 'Look at your ball, pinch to place the tee',
      'setting-aim': 'Look downrange, pinch to aim',
    };
    gameContext.hint?.setText(state === 'anchored' ? readyHintText() : (messages[state] ?? ''));
    if (state !== 'anchored') {
      gameContext.groundHud?.setVisible(false);
      gameContext.clubOverlay?.showAlignment(false);
    }
  });

  gameContext.anchorController.on('hint', text => gameContext.hint?.setText(text));

  // Everything that has to follow the tee wherever it ends up. Split out from
  // the 'anchored' handler because the adjust stage republishes the pose on
  // every stick step: this part is safe to repeat, the setup below it isn't.
  const applyTeePose = ({ position, yaw }: { position: THREE.Vector3; yaw: number }) => {
    // positionWorld (after the worldRoot transform) lives in reference space,
    // so the mask and the yardage projection both follow the anchored tee
    const forward = new THREE.Vector3(Math.sin(yaw), 0, Math.cos(yaw));
    gameContext.worldMask?.update(position, forward);
    gameContext.worldMask?.setEnabled(true);
    // Window: manual rect applies immediately; auto re-solves for every fresh
    // anchor (the aim line moved, so the walls' roles may have changed too)
    gameContext.anchorYaw = yaw;
    gameContext.windowSolved = false;
    if (gameContext.windowMode === 'manual') {
      applyWindow(gameContext.windowRect ?? null, true);
    } else if (gameContext.windowMode === 'off') {
      applyWindow(null);
    }
    gameContext.yardageLines?.setFrame(position, position.clone().add(forward));
    gameContext.statsPanel?.placeAtTee(position, yaw, gameContext.handedSide);
    gameContext.groundHud?.placeAtTee(position, yaw);
    // Across the tee, beyond the stats panel — the direction the player faces
    // at address, so it stays clear of the ball path and the downrange view
    gameContext.shotTv?.placeAtTee(position, yaw, gameContext.handedSide);
    // Swing mode aims by eye at a virtual ball — the floor line is the only
    // alignment aid the player has
    gameContext.clubOverlay?.placeAlignment(position, yaw);
    gameContext.clubOverlay?.showAlignment(isSwingMode());
    updateChaseFrame(position, position.clone().addScaledVector(forward, 200));
  };

  gameContext.anchorController.on('teeMoved', applyTeePose);

  gameContext.anchorController.on('anchored', pose => {
    applyTeePose(pose);
    // Entering MR straight into swing mode lands here rather than in setMode, and
    // an uncalibrated club is drawn from a guess — offer the outline now.
    // Strictly once-per-anchor: this parks an outline and takes the trigger, so
    // it must never run off a pose republish.
    //
    // Not in immersive mode, where placement is immediately followed by the tee
    // adjust stage: two flows would be up at once, both wanting the trigger and
    // each overwriting the other's hint. endTeeAdjust makes the offer instead,
    // once the tee is settled.
    if (!gameContext.immersive && isSwingMode() && !gameContext.swingControls?.isCalibrated) {
      beginClubAlignment();
    }
  });

  gameContext.xrSession.on('sessionstart', async () => {
    const session = gameContext.xrSession!.session!;
    gameContext.worldRoot!.visible = false; // hidden until anchored
    if (gameContext.immersive) {
      // Nothing to anchor to and nothing to line the tee up with — the real
      // ball and mat the pinch flow exists to register against aren't there.
      // Place at the default address pose, through the same code path a
      // completed pinch flow ends at, then hand the player the adjust stage.
      //
      // Placed FIRST and adjusted after, rather than aimed from a reticle: the
      // world is hidden until anchored, so a place-then-confirm flow would have
      // them positioning a marker in an empty void with no ground or range to
      // judge it against. Passthrough gets away with that; VR can't.
      placeTeeByDefault();
      beginTeeAdjust();
    } else {
      try {
        // begin() never dead-ends: without hit-test it falls back to
        // head-relative placement and the pinch flow still completes
        await gameContext.anchorController!.begin(session);
      } catch (error) {
        console.error('Anchoring failed to start', error);
        gameContext.hint?.setText('Anchoring unavailable — try re-entering MR');
      }
    }
    // Compile every shader while the player is still placing the tee — the
    // alternative is a pipeline-compile hitch mid-flight of the first shot
    warmupShaders().catch(error => console.warn('Shader warm-up failed', error));
  });

  gameContext.xrSession.on('referencereset', () => {
    // Recenter with a live anchor self-corrects; without one the stored
    // transform is absolute and the world just teleported — re-place it
    if (gameContext.anchorController?.isAnchored && !gameContext.anchorController.hasAnchor) {
      gameContext.hint?.setText('View recentered — pinch to re-place the tee');
      gameContext.worldMask?.setEnabled(false);
      gameContext.anchorController.restart();
    }
  });

  gameContext.xrSession.on('sessionend', () => {
    // Back to the desktop preview: world at identity, mask per sim mode
    gameContext.worldRoot!.visible = true;
    gameContext.worldRoot!.matrix.identity();
    gameContext.worldRoot!.matrixWorldNeedsUpdate = true;
    gameContext.worldMask?.setEnabled(gameContext.simMode);
    gameContext.worldMask?.update(gameContext.startPoint, new THREE.Vector3(0, 0, 1));
    // The auto-solved window belonged to the session's anchor — drop it (a
    // manual rect survives; the sim preview reapplies its own)
    gameContext.windowSolved = false;
    if (gameContext.windowMode === 'auto') applyWindow(null);
    gameContext.yardageLines?.setFrame(gameContext.startPoint, gameContext.aimPoint);
    gameContext.statsPanel?.placeAtTee(gameContext.startPoint, 0, gameContext.handedSide);
    gameContext.groundHud?.placeAtTee(gameContext.startPoint, 0);
    gameContext.shotTv?.placeAtTee(gameContext.startPoint, 0, gameContext.handedSide);
    stopTvChase({ hide: true });
    updateChaseFrame();
    gameContext.hint?.setText('');
    gameContext.confirmRequested = false;
    gameContext.pendingShot = undefined;
    gameContext.monitorLive = false; // fresh session, fresh assumption
    refreshMonitorStatus();
    gameContext.infoPanel?.close();
    gameContext.xrControls?.reset();
    gameContext.wristMenu?.reset();
    gameContext.actionMenu?.reset();
    // Dropped without the usual follow-on: the session is over, so there is no
    // club outline to offer and no hint anyone will read
    gameContext.teeAdjust = undefined;
    gameContext.teeStick?.reset();
    gameContext.swingControls?.reset();
    gameContext.controllerRays?.reset();
    gameContext.modeDetector?.reset();
    gameContext.clubOverlay?.setVisible(false);
    gameContext.clubOverlay?.showAlignment(false);
    gameContext.clubGhost?.setVisible(false);
    gameContext.rayTarget = undefined;
    const button = document.getElementById('enter-mr') as HTMLButtonElement | null;
    if (button) button.disabled = false;
  });

  gameContext.xrControls.on('confirm', () => {
    // A trigger pull with a ray resting on a panel is a click, not a shot
    if (gameContext.rayTarget === 'info' && gameContext.infoPanel?.activateHover()) return;
    if (gameContext.rayTarget === 'menu' && gameContext.wristMenu?.activateHover()) return;
    if (gameContext.rayTarget === 'actions' && gameContext.actionMenu?.activateHover()) return;
    // Positioning the tee owns the trigger while it's up — that press is the
    // "done" it's waiting for, and must not also fire a shot at the tee it just
    // finished placing
    if (gameContext.teeAdjust) {
      endTeeAdjust();
      return;
    }
    // A pinch near an open panel is a poke gone wide — never a shot
    if (panelOpen()) return;
    // Calibration owns the trigger while it's up, ahead of anchoring: the
    // outline can be lined up before the tee is placed.
    if (advanceClubAlignment()) return;
    const state = gameContext.anchorController!.state;
    if (state === 'placing-ball' || state === 'setting-aim') {
      // XRFrame is only valid inside the animation loop — defer
      gameContext.confirmRequested = true;
    } else if (isSwingMode()) {
      // In swing mode the swing is the shot; nothing else for the trigger to do
      if (state === 'anchored') gameContext.hint?.setText(readyHintText());
    } else if (state === 'anchored') {
      if (testShotsAllowed()) {
        gameContext.xrControls!.fireCurrentPreset();
      } else {
        // A live monitor owns the shots — a stray pinch must never fire a
        // phantom ball. Say so instead of silently ignoring the gesture.
        gameContext.hint?.setText('Monitor active — practice shots off (?testshots=1 to enable)');
      }
    }
  });
  gameContext.xrControls.on('guardChanged', active => {
    if (!gameContext.anchorController?.isAnchored) return;
    // Gripping the club — get out of the player's face
    gameContext.hint?.setText(active ? '' : readyHintText());
  });
  gameContext.xrControls.on('testShot', shot => launchShot(shot));
  gameContext.xrControls.on('presetChanged', (_index, name) => {
    gameContext.presetName = name;
    // Through the resolver: cycling presets must not paint over a club that is
    // actually in the player hands.
    gameContext.statsPanel?.setPresetLabel(clubLabel());
    // The side panel is off to the side — the head hint is what the player
    // actually sees while cycling presets by pinching
    gameContext.hint?.setText(`Club: ${name}`);
  });
  gameContext.xrControls.on('reanchor', () => {
    // A held trigger while calibrating is "start over on the outline", not a
    // tee re-place — with a club in the hands it's the natural redo gesture
    const swing = gameContext.swingControls;
    if (swing && swing.alignmentState !== 'off') {
      swing.cancelAlignment();
      beginClubAlignment();
      return;
    }
    if (!gameContext.anchorController?.isAnchored) return;
    // Two-step: an accidental 2 s pinch must not destroy the alignment
    const now = performance.now();
    if (!gameContext.reanchorArmedUntil || now > gameContext.reanchorArmedUntil) {
      gameContext.reanchorArmedUntil = now + 5000;
      // Reached by a held pinch, a held trigger or a held squeeze — don't name
      // the gesture, since with a club in hand it isn't a pinch
      gameContext.hint?.setText('Hold again to re-place the tee');
      return;
    }
    gameContext.reanchorArmedUntil = undefined;
    gameContext.swingControls?.cancelAlignment();
    gameContext.worldMask?.setEnabled(false);
    gameContext.anchorController.restart();
  });

  // Desktop-visible test shot (DOM is invisible in-session; pinch covers MR)
  document.getElementById('test-shot')?.addEventListener('click', () => {
    gameContext.audioPlayer?.unlock().then(() => {
      gameContext.windAmbience?.start();
      gameContext.windAmbience?.setWind(gameContext.windMph);
    }).catch(() => {});
    gameContext.xrControls?.fireCurrentPreset();
  });

  const button = document.getElementById('enter-mr') as HTMLButtonElement | null;
  button?.addEventListener('click', async () => {
    button.disabled = true;
    // The one guaranteed user gesture — unlock audio here or never
    gameContext.audioPlayer?.unlock().then(() => {
      gameContext.windAmbience?.start();
      gameContext.windAmbience?.setWind(gameContext.windMph);
    }).catch(() => {});
    try {
      await gameContext.xrSession!.start();
    } catch (error) {
      console.error('Failed to start XR session', error);
      if (statusEl()) statusEl()!.textContent = `Could not start MR session: ${error}`;
      button.disabled = false;
    }
  });
}

/**
 * Virtual-golf mode: the club drawn on the controller, the swept-strike detector
 * that turns a real swing into launch data, and the controller rays that drive
 * the panels once both hands are busy holding a club.
 *
 * Built unconditionally — it costs three small objects and no per-frame work
 * until `setMode('swing')` — so switching modes mid-session never has to
 * construct anything or compile a shader.
 */
function setupSwingMode(scene: THREE.Scene) {
  gameContext.clubOverlay = new ClubOverlay({ clubHand: gameContext.clubHand });
  scene.add(gameContext.clubOverlay.object);
  gameContext.clubGhost = new ClubOverlay({ clubHand: gameContext.clubHand, ghost: true });
  gameContext.clubGhost.setVisible(false);
  scene.add(gameContext.clubGhost.object);
  gameContext.controllerRays = new ControllerRays();
  scene.add(gameContext.controllerRays.object);

  const swing = new XRSwingControls({
    clubHand: gameContext.clubHand,
    store: gameContext.calibrationStore,
    ballWorld: ballWorldPosition,
    targetDir: targetDirection,
    // Reaching for a ray-driven panel waves the club about, which is not a shot.
    // (Calibration gates itself — see XRSwingControls.alignmentState.)
    isBlocked: panelOpen,
    // Floor for the ground rule: the anchored tee carries the real mat height
    // from the hit test; identity (0) in the preview and between sessions.
    groundY: () => gameContext.teeWorld.y,
    captureRadius: gameContext.swingCaptureRadius,
    attachment: gameContext.swingAttachment,
    buttOffset: gameContext.swingButtOffset,
    shaftAxis: gameContext.swingShaftAxis,
    faceAxis: gameContext.swingFaceAxis,
    initialClub: DEFAULT_SWING_CLUB,
  });
  swing.debug = gameContext.swingDebug;
  gameContext.swingControls = swing;
  // Pull any calibration made on another device or origin. Fire and forget, at
  // startup only: this can replace the club in the player's hands, which is
  // fine before a session and alarming in the middle of one.
  swing.syncCalibration()
    .then(changed => {
      if (changed) console.log('Club calibration restored from the cloud profile');
      refreshMonitorStatus();
    })
    .catch(() => { /* store already records the failure for the status row */ });
  gameContext.swingClubName = swing.club.name;
  gameContext.clubOverlay.setGeometry(swing.buttLocal, swing.headLocal, swing.shaftLocal, swing.faceLocal);
  gameContext.clubOverlay.setClub(swing.club);
  gameContext.clubGhost?.setClub(swing.club);

  swing.on('impact', launchSwing);
  swing.on('whiff', () => gameContext.hint?.setText('Whiff — swing through the ball'));
  swing.on('clubChanged', (_index, club) => {
    gameContext.swingClubName = club.name;
    gameContext.clubOverlay?.setClub(club);
    gameContext.clubGhost?.setClub(club);
    gameContext.statsPanel?.setPresetLabel(clubLabel());
    // The side panel is off to the side — the head hint is what the player sees
    gameContext.hint?.setText(`Club: ${club.name}`);
  });
  swing.on('geometryChanged', (buttLocal, headLocal, shaftLocal, faceLocal) => {
    gameContext.clubOverlay?.setGeometry(buttLocal, headLocal, shaftLocal, faceLocal);
  });
  swing.on('alignmentChanged', state => {
    gameContext.hint?.setText(alignmentHint(state));
    // The outline shows what to match; the live club is drawn from the geometry
    // being replaced, so showing both while aligning is just two wrong clubs.
    // (In 'tuning' the live club is exactly what's being trimmed — no ghost.)
    gameContext.clubGhost?.setVisible(state === 'aligning');
    if (state === 'off') refreshMonitorStatus();
  });
  swing.on('tuningChanged', () => gameContext.hint?.setText(alignmentHint('tuning')));
  swing.on('attachmentChanged', (attachment, calibrated) => {
    gameContext.swingAttachment = attachment.id;
    refreshMonitorStatus();
    gameContext.hint?.setText(calibrated
      ? `${attachment.label} — saved calibration restored`
      : `${attachment.label} — calibrate to set the club length`);
  });
  swing.on('calibrated', () => {
    refreshMonitorStatus();
    // The explicit post-lock confirmation moment — the solved club is already
    // in the hand (geometryChanged fired first)
    gameContext.hint?.setText('Club calibrated — take a practice swing');
  });
  swing.on('button', id => {
    if (id === 'club') {
      // While fine-tuning, A/X is the ball snap — the precise way to fix
      // length once the tee is placed
      if (swing.alignmentState === 'tuning') calibrateFromBall();
      else if (swing.alignmentState === 'off') swing.cycleClub(1);
    } else if (swing.alignmentState !== 'off') {
      swing.cancelAlignment();
      gameContext.hint?.setText(readyHintText());
    } else if (gameContext.actionMenu?.isOpen) {
      gameContext.actionMenu.close();
    } else {
      // Controller mode gets the full menu, not the wrist panel parked on a ray
      gameContext.infoPanel?.close();
      gameContext.wristMenu?.close();
      gameContext.actionMenu?.open(getHeadPose(!!gameContext.renderer?.isPresenting));
    }
  });
}

function setupInput() {
  gameContext.controls = new CourseKeyboardControls({ testShots: true });
  gameContext.controls.on('testShot', shot => launchShot(shot));

  // Desktop swing-mode helpers. There is no controller to swing in the preview,
  // and the impact model is the part most worth checking without a headset — V
  // switches modes, B fires a plausible swing through the real model.
  // (S/M/F are already taken by CourseKeyboardControls.)
  window.addEventListener('keydown', event => {
    if (gameContext.renderer?.isPresenting) return;
    if (event.code === 'KeyV') {
      setMode(isSwingMode() ? 'monitor' : 'swing');
      console.log(`mode = ${gameContext.mode}`);
    } else if (event.code === 'KeyB' && isSwingMode()) {
      fireSyntheticSwing();
    } else if (event.code === 'KeyG' && gameContext.camera) {
      // Action-menu layout preview — G, because the panel's real binding is the
      // controller's B and that already fires a synthetic swing here. Lives in
      // this handler rather than the mask-tuning one below so it works with no
      // portal to tune, which is the case in the immersive mode.
      gameContext.actionMenu?.forceOpenAt(gameContext.camera);
    }
  });

  // Mask tuning in the desktop preview: [ ] portal distance, - = disc size, X colorize
  window.addEventListener('keydown', event => {
    const mask = gameContext.worldMask;
    if (!mask || !gameContext.simMode) return;
    switch (event.code) {
      case 'BracketLeft': mask.uPortalDist.value = Math.max(0, mask.uPortalDist.value - 0.1); break;
      case 'BracketRight': mask.uPortalDist.value += 0.1; break;
      case 'Minus': mask.uDiscRadii.value.multiplyScalar(0.95); break;
      case 'Equal': mask.uDiscRadii.value.multiplyScalar(1.05); break;
      case 'KeyX': mask.debugColorize(!(gameContext as any).maskDebug); (gameContext as any).maskDebug = !(gameContext as any).maskDebug; break;
      case 'KeyN':
        // Window preview toggle — orbit the camera to check the parallax
        if (mask.windowEnabled) applyWindow(null);
        else applyWindow(gameContext.windowRect ?? { dist: 3.5, left: 2.2, right: 2.2, top: 2.4 });
        break;
      case 'KeyW':
        // Layout preview: park the menu in front of the camera (no hands to poke)
        if (gameContext.camera) gameContext.wristMenu?.forceOpenAt(gameContext.camera);
        break;
      case 'KeyH':
        // Help-panel layout preview — same reason: no hands on the desktop
        if (gameContext.camera) gameContext.infoPanel?.toggle(gameContext.camera);
        break;
      default: return;
    }
    console.log(`portal=${mask.uPortalDist.value.toFixed(2)}m disc=${mask.uDiscRadii.value.x.toFixed(2)}x${mask.uDiscRadii.value.y.toFixed(2)}m`);
  });
}

/**
 * Compile the shaders of everything that appears later (HUD, trail, ball,
 * world) without visible flashes: force objects visible with color writes
 * off, compile, restore. compileAsync skips visible:false objects, which is
 * exactly why the trail/HUD would otherwise hitch on first appearance.
 */
async function warmupShaders() {
  const renderer = gameContext.renderer;
  const scene = gameContext.scene;
  const camera = gameContext.camera;
  if (!renderer || !scene || !camera) return;

  gameContext.golfBall?.ensureTrail();

  const roots: THREE.Object3D[] = [
    gameContext.worldRoot!,   // the sky dome rides along as a child of this
    gameContext.groundHud!.object,
    gameContext.statsPanel!.object,
    gameContext.hint!.object,
    gameContext.wristMenu!.object,
    gameContext.actionMenu!.object,
    gameContext.shotTv!.object,
    gameContext.infoPanel!.object,
    // All start hidden, and a mid-session mode switch must not hitch
    gameContext.clubOverlay!.object,
    gameContext.clubGhost!.object,
    gameContext.controllerRays!.object,
  ].filter(Boolean);

  const visibilityChanges: { object: THREE.Object3D; visible: boolean }[] = [];
  const materialChanges: { material: any; colorWrite: boolean }[] = [];
  const seenMaterials = new Set<any>();

  for (const root of roots) {
    root.traverse((object: any) => {
      if (!object.visible) {
        visibilityChanges.push({ object, visible: false });
        object.visible = true;
      }
      const materials = Array.isArray(object.material) ? object.material : object.material ? [object.material] : [];
      for (const material of materials) {
        if (seenMaterials.has(material)) continue;
        seenMaterials.add(material);
        materialChanges.push({ material, colorWrite: material.colorWrite });
        material.colorWrite = false;
      }
    });
  }

  try {
    await (renderer.renderer as any).compileAsync(scene, camera);
  } finally {
    for (const { object, visible } of visibilityChanges) object.visible = visible;
    for (const { material, colorWrite } of materialChanges) material.colorWrite = colorWrite;
  }
}

function onWristMenuSelect(id: string) {
  playShotBlip(); // tap feedback
  switch (id) {
    case 'club-prev':
      if (isSwingMode()) gameContext.swingControls?.cycleClub(-1);
      else gameContext.xrControls?.cyclePreset(-1);
      break;
    case 'club-next':
      if (isSwingMode()) gameContext.swingControls?.cycleClub(1);
      else gameContext.xrControls?.cyclePreset(1);
      break;
    case 'teeadjust':
      beginTeeAdjust();
      break;
    case 'sync':
      // Manual retry, for the case the row is reporting: the automatic sync
      // runs once at startup, so a network that came back since then needs
      // asking again rather than a reload
      gameContext.swingControls?.syncCalibration()
        .then(changed => gameContext.hint?.setText(changed
          ? 'Club calibration restored from your profile'
          : 'Calibration profile is up to date'))
        .catch(() => gameContext.hint?.setText('Could not reach the calibration store'));
      break;
    case 'vrmode': {
      // A session's mode is fixed at requestSession, so there is no in-session
      // switch between passthrough and full VR to make: flip the ?vr= flag and
      // come back through the entry button, which is also the user gesture a
      // fresh session needs. Ending first for the same reason 'reload' does —
      // navigating out of a live immersive session leaves the compositor in a
      // bad state.
      const url = new URL(window.location.href);
      if (gameContext.immersive) url.searchParams.delete('vr');
      else url.searchParams.set('vr', '1');
      (gameContext.xrSession?.end() ?? Promise.resolve())
        .catch(() => {})
        .finally(() => window.location.assign(url.toString()));
      break;
    }
    case 'playcourse': {
      // A sibling page, not a mode: the course is a different world and a 58 MB
      // download, neither of which belongs in the range's footprint unasked.
      // Query flags ride along — they were typed once on a phone keyboard.
      const target = new URL('../course-mr/', window.location.href).pathname
        + window.location.search;
      (gameContext.xrSession?.end() ?? Promise.resolve())
        .catch(() => {})
        .finally(() => { window.location.href = target; });
      break;
    }
    case 'reload':
      // On-headset deploy pickup. End the session first — reloading from
      // inside an immersive session leaves the compositor in a bad state.
      (gameContext.xrSession?.end() ?? Promise.resolve())
        .catch(() => {})
        .finally(() => window.location.reload());
      break;
    case 'action':
      if (isSwingMode()) {
        beginClubAlignment();
      } else if (testShotsAllowed()) {
        gameContext.xrControls?.fireCurrentPreset();
      } else {
        gameContext.hint?.setText('Monitor active — practice shots off (?testshots=1 to enable)');
      }
      break;
    case 'reanchor':
      // A menu tap is deliberate — no two-step confirm needed. Closing first is
      // essential with a ray-driven panel: re-placing the tee needs the very
      // trigger an open panel swallows, so leaving it up would strand the player
      // in 'placing-ball' with no way to confirm.
      closePanels();
      gameContext.swingControls?.cancelAlignment();
      gameContext.worldMask?.setEnabled(false);
      if (gameContext.immersive) {
        // No room to re-register against, so re-placing means back to the
        // default address pose in front of wherever the player now stands, then
        // straight into the adjust stage. Sending them through the pinch flow
        // instead would ask them to aim at a real ball that isn't there.
        placeTeeByDefault();
        beginTeeAdjust();
      } else {
        gameContext.anchorController?.restart();
      }
      break;
    case 'units': {
      if (gameContext.setupData) {
        const units = gameContext.setupData.units === 'metric' ? 'imperial' : 'metric';
        gameContext.setupData.units = units;
        if (gameContext.groundHud) gameContext.groundHud.units = units;
        if (gameContext.statsPanel) gameContext.statsPanel.units = units;
      }
      break;
    }
    case 'help':
      if (gameContext.infoPanel && gameContext.camera) {
        gameContext.infoPanel.toggle(getHeadPose(!!gameContext.renderer?.isPresenting));
      }
      break;
    case 'tv':
      if (gameContext.tvMode === 'off') {
        // Back to whatever the URL asked for, but never back to 'off'
        gameContext.tvMode = gameContext.tvModeFromUrl === 'off' ? 'auto' : gameContext.tvModeFromUrl;
      } else {
        gameContext.tvMode = 'off';
        stopTvChase({ hide: true });
      }
      break;
    case 'aux':
      // The wrist menu's shared slot: attachment in swing mode, perf otherwise.
      // The action menu has room to offer both outright, below.
      if (isSwingMode()) {
        gameContext.swingControls?.cycleAttachment(1);
      } else {
        gameContext.perfHud = !gameContext.perfHud;
        if (!gameContext.perfHud) gameContext.statsPanel?.setPresetLabel(clubLabel());
      }
      break;
    case 'grip':
      gameContext.swingControls?.cycleAttachment(1);
      break;
    case 'perf':
      gameContext.perfHud = !gameContext.perfHud;
      if (!gameContext.perfHud) gameContext.statsPanel?.setPresetLabel(clubLabel());
      break;
    case 'exit':
      gameContext.xrSession?.end().catch(() => {});
      break;
  }
}

function onShotEnded() {
  // Stop the offscreen pass but leave the picture up: the render target keeps
  // its last contents, giving a free freeze-frame of the landing that holds
  // until the next shot, the same way the trail's arc does.
  stopTvChase();
  setupNextShot();
  try {
    app.sendShotResult({
      shot: gameContext.golfBall?.lastShot,
      stats: gameContext.golfBall?.stats,
      player: gameContext.currentPlayer?.player,
      club: gameContext.currentPlayer?.currentClub,
    });
  } catch (error) {
    console.warn('Could not send shot result', error);
  }
  // Fire a queued practice shot now that the ball is back on the tee
  const pending = gameContext.pendingShot;
  if (pending) {
    gameContext.pendingShot = undefined;
    launchShot(pending);
  }
}

function setupNextShot() {
  gameContext.golfBall?.reset(gameContext.aimPoint, gameContext.startPoint);
  if (gameContext.currentPlayer) {
    app.sendPlayerUpdate(gameContext.currentPlayer, gameContext.startPoint.toArray());
  }
}

const ballWorldPos = new THREE.Vector3();

/**
 * Drive the chase camera and draw it into the TV. This is a second full scene
 * pass, so it runs only while a qualifying shot is in the air, and only on
 * every other frame — the picture is a ~1 m screen, where 36 Hz is invisible,
 * whereas dropped frames in MR are reprojection judder.
 */
function updateTv(delta: number, sessionVisible: boolean) {
  const tv = gameContext.shotTv;
  const chaseCamera = gameContext.chaseCamera;
  const ball = gameContext.golfBall;
  const scene = gameContext.scene;
  if (!tv || !chaseCamera || !ball || !scene || !gameContext.tvChasing || !sessionVisible) return;

  // The ball lives under worldRoot; the chase camera is unparented, so it has
  // to be fed the ball's world position rather than its local one.
  ball.object.getWorldPosition(ballWorldPos);
  chaseCamera.track(delta, gameContext.teeWorld, ballWorldPos);
  // track() assigns cameraTrackingFov but never applies it (src/camera.ts:278)
  chaseCamera.updateProjectionMatrix();
  chaseCamera.updateMatrixWorld();

  gameContext.tvFrame++;
  if (gameContext.tvFrame % 2 !== 0) return;

  const ok = tv.render({
    renderer: gameContext.renderer!,
    scene,
    camera: chaseCamera,
    // Reference-space UI would otherwise float inside the picture
    hide: [
      tv.object,
      gameContext.statsPanel?.object,
      gameContext.groundHud?.object,
      gameContext.hint?.object,
      gameContext.wristMenu?.object,
      gameContext.infoPanel?.object,
      // The club sits at the player's hands — from 18 m behind the ball it would
      // be a girder across the picture
      gameContext.clubOverlay?.object,
      gameContext.clubGhost?.object,
      gameContext.controllerRays?.object,
    ].filter(Boolean) as THREE.Object3D[],
    worldMask: gameContext.worldMask,
  });
  if (!ok) stopTvChase({ hide: true });
}

/** Keep the drawn club on the controller that's carrying it. */
function updateClubOverlay() {
  const swing = gameContext.swingControls;
  const overlay = gameContext.clubOverlay;
  if (!swing || !overlay) return;

  const aligning = swing.alignmentState === 'aligning';
  overlay.setVisible(swing.hasGrip && !aligning);
  if (swing.hasGrip) {
    overlay.setPose(swing.gripPosition, swing.gripQuaternion);
    // Ground rule: draw the club exactly where the physics says it is —
    // slid up its shaft, never through the floor
    overlay.setSlide(swing.groundSlide);
  }

  // The outline is parked in the room, so its pose only needs setting while it's
  // up — but its canonical geometry has to follow the length being aligned to.
  const ghost = gameContext.clubGhost;
  const pose = swing.alignmentPose;
  if (!ghost) return;
  if (pose) {
    ghostHead.set(0, 0, -pose.length);
    ghost.setGeometry(ghostButt, ghostHead, ghostShaft, ghostFace);
    ghost.setPose(pose.position, pose.quaternion);
  }
  ghost.setVisible(!!pose);
}

// Canonical outline geometry: butt at the pose origin, shaft down -Z, face +X
// (see XRSwingControls.alignmentPose). The head param only feeds the drawn
// length via its along-shaft projection; the face-centre lateral offset is the
// overlay's own business.
const ghostButt = new THREE.Vector3(0, 0, 0);
const ghostHead = new THREE.Vector3(0, 0, -1);
const ghostShaft = new THREE.Vector3(0, 0, -1);
const ghostFace = new THREE.Vector3(1, 0, 0);

/**
 * Controller rays → panel hover, and the pointer dot resting on the surface.
 * Only runs in swing mode: monitor mode is hands-only by design and a laser
 * across the mat there would be noise.
 */
function updateRayPanels(delta: number, frame: XRFrame, referenceSpace: XRReferenceSpace) {
  const rays = gameContext.controllerRays;
  const controls = gameContext.xrControls;
  if (!rays || !controls) return;

  rays.update(frame, referenceSpace, controls.inputSources);
  gameContext.rayTarget = undefined;

  const info = gameContext.infoPanel;
  const menu = gameContext.wristMenu;
  const actions = gameContext.actionMenu;
  const active = !!info?.isOpen || !!menu?.isRayOpen || !!actions?.isOpen;
  rays.setVisible(active && rays.rays.length > 0);
  if (!active) {
    rays.setHit();
    // Still tick the action menu so its idle-close timer keeps running while
    // it's shut — its clock is the only thing that can reopen the question
    actions?.updateRay(delta, []);
    return;
  }

  // The help panel is the front-most surface whenever it's up — it's opened from
  // a menu and parked between the player and it. The action menu comes next; the
  // wrist panel is last, being the fallback the other two replace.
  const target = info?.isOpen ? 'info' : actions?.isOpen ? 'actions' : 'menu';
  const hit = target === 'info'
    ? info!.updateRay(delta, rays.rays)
    : target === 'actions'
      ? actions!.updateRay(delta, rays.rays)
      : menu?.updateRay(delta, rays.rays);
  if (hit) {
    rays.setHit(hit.rayIndex, hit.distance);
    gameContext.rayTarget = target;
  } else {
    rays.setHit();
  }
}

const headMidpoint = new THREE.Vector3();

/** Head pose for billboards/hints: eye midpoint, not the left eye. */
function getHeadPose(presenting: boolean): { position: THREE.Vector3; quaternion: THREE.Quaternion } {
  if (!presenting) return gameContext.camera!;
  const xrCam = gameContext.renderer!.xr.getCamera();
  const cameras = xrCam.cameras;
  if (cameras?.length === 2) {
    headMidpoint.copy(cameras[0].position).add(cameras[1].position).multiplyScalar(0.5);
    return { position: headMidpoint, quaternion: xrCam.quaternion };
  }
  return xrCam;
}

/** In-session degradation: dropped frames in MR are reprojection judder
 *  (nausea) — foveation is the one lever adjustable mid-session. */
function checkPerformance(time: number, deltaMs: number) {
  gameContext.frameEmaMs = gameContext.frameEmaMs * 0.95 + Math.min(deltaMs, 100) * 0.05;
  if (time - gameContext.lastPerfCheckAt < 3000) return;
  gameContext.lastPerfCheckAt = time;

  const session: any = gameContext.xrSession?.session;
  const budgetMs = 1000 / (session?.frameRate ?? 72);
  if (gameContext.frameEmaMs > budgetMs * 1.15 && gameContext.foveation < 1) {
    gameContext.foveation = Math.min(1, gameContext.foveation + 0.2);
    gameContext.renderer!.xr.setFoveation(gameContext.foveation);
    console.warn(`Frame budget exceeded (${gameContext.frameEmaMs.toFixed(1)}ms) — foveation → ${gameContext.foveation.toFixed(1)}`);
  }
  if (gameContext.perfHud) {
    gameContext.statsPanel?.setPresetLabel(`${clubLabel()} · ${gameContext.frameEmaMs.toFixed(1)}ms`);
  }
}

function animate(time: number, frame?: XRFrame) {
  const delta = gameContext.timer.getDelta();
  const renderer = gameContext.renderer!;
  const presenting = renderer.isPresenting;
  const head = getHeadPose(presenting);

  // Headset doffed / browser hidden: don't fly the shot unseen
  const sessionVisible = !presenting
    || (gameContext.xrSession?.session as any)?.visibilityState !== 'hidden';

  if (frame && presenting && gameContext.xrSession?.referenceSpace) {
    const referenceSpace = gameContext.xrSession.referenceSpace;
    if (gameContext.confirmRequested) {
      gameContext.confirmRequested = false;
      gameContext.anchorController?.confirm(frame, referenceSpace);
    }
    gameContext.anchorController?.update(frame, referenceSpace);
    gameContext.xrControls?.update(delta, frame, referenceSpace);
    if (gameContext.xrControls) {
      // Right after xrControls.update so inputSources and hand trackers are fresh
      gameContext.modeDetector?.update(
        delta, frame, referenceSpace,
        gameContext.xrControls.inputSources, gameContext.xrControls.hands,
      );
      applyAutoMode();
      // Before the panels: the adjust stage closes them on entry, and its stick
      // is read straight off the input sources rather than through swing mode,
      // which doesn't update in launch-monitor mode
      updateTeeAdjust(delta);
      gameContext.wristMenu?.update(delta, gameContext.xrControls.hands, gameContext.xrControls.guard, head);
      gameContext.infoPanel?.update(delta, gameContext.xrControls.hands);
      if (isSwingMode()) {
        gameContext.swingControls?.update(delta, frame, referenceSpace, gameContext.xrControls.inputSources);
        updateClubOverlay();
        // Walked away from the parked outline? Bring it to the new stance —
        // nobody should have to fail a pull to get the ghost back in reach.
        const aligning = gameContext.swingControls?.alignmentState === 'aligning';
        const outline = gameContext.swingControls?.alignmentPose;
        if (aligning && outline) {
          const dx = head.position.x - outline.position.x;
          const dz = head.position.z - outline.position.z;
          if (dx * dx + dz * dz > 1.2 * 1.2) beginClubAlignment();
        }
        // After the poke pass, so a ray wins when both are available: the hand
        // path clears its hover when no finger is near, then this sets it.
        updateRayPanels(delta, frame, referenceSpace);
      }
    }
    checkPerformance(time, delta * 1000);
  }

  // Tracer near-fade reference. Fed as a uniform rather than read from the
  // camera, and deliberately the eye MIDPOINT: a per-eye position would fade
  // the trail at a different place in each eye.
  gameContext.golfBall?.trail?.setViewerPosition(head.position);
  // …and every shader that measures a distance to the player. They all used the
  // TSL `cameraPosition` built-in, which breaks under XR's ArrayCamera — see
  // src/shaders/viewer.ts.
  setViewerPosition(head.position);
  // Same midpoint drives the window's through-the-frame projection (and the
  // desktop camera in the sim preview, which makes the parallax testable)
  gameContext.worldMask?.setEye(head.position);
  // …and the audio listener, so spatialized landings come from the right place
  gameContext.audioPlayer?.updateListener(head.position, head.quaternion);

  // Flag cloth — only while the virtual world is actually on show
  if (sessionVisible && gameContext.worldRoot?.visible && gameContext.flagSticks) {
    for (const stick of gameContext.flagSticks) stick.update(delta);
  }

  // Auto window: poll the room planes until the walls/ceiling resolve. Planes
  // arrive late and Space Setup can change mid-session, so this is per-frame
  // until it lands, then never again for this anchor.
  if (
    frame && presenting
    && !gameContext.immersive // no room, no walls to frame a window in
    && gameContext.windowMode === 'auto'
    && !gameContext.windowSolved
    && gameContext.anchorController?.isAnchored
    && gameContext.xrSession?.referenceSpace
  ) {
    const rect = gameContext.roomBounds?.solve(frame, gameContext.xrSession.referenceSpace, {
      position: gameContext.teeWorld,
      yaw: gameContext.anchorYaw,
    });
    if (rect) {
      gameContext.windowSolved = true;
      applyWindow(rect, true);
    }
  }

  if (sessionVisible) {
    gameContext.golfBall?.update(delta);
  }

  if (gameContext.golfBall) {
    const ball = gameContext.golfBall;
    // In MR the real ball is on the mat — showing the virtual twin on top of
    // it reads as a double ball. Render the sim ball only once it's in flight
    // and clear of the tee; the contact shadow carries the depth cue.
    const inFlight = ball.isShotActive
      && ball.object.position.distanceToSquared(ball.startPoint) > 0.09;
    if (presenting) {
      // …except in swing mode, where the ball on the tee IS the thing being hit,
      // so it has to be there to aim at — and except in immersive mode, where
      // there is no real ball for it to double up with and hiding it would
      // leave the player addressing bare turf.
      ball.object.visible = inFlight
        || gameContext.immersive
        || (isSwingMode() && !!gameContext.anchorController?.isAnchored);
    }
    gameContext.contactShadow?.update(ball.object.position, inFlight || (!presenting && ball.isShotActive));
  }

  if (gameContext.golfBall) {
    gameContext.groundHud?.update(gameContext.golfBall.stats, gameContext.golfBall.lastShot);
    gameContext.statsPanel?.update(gameContext.golfBall.stats, gameContext.golfBall.lastShot);
    gameContext.statsPanel?.face(head.position);
    gameContext.hint?.update(delta, head.position, head.quaternion);
  }

  updateTv(delta, sessionVisible);

  if (gameContext.scene && gameContext.camera) {
    renderer.render(gameContext.scene, gameContext.camera); // no fog arg — MR keeps scene.fog null
  }

  gameContext.timer.update(time);
}

async function preLoad() {
  const params = new URLSearchParams(window.location.search);

  gameContext.simMode = params.get('sim') === '1' || params.get('sim') === 'true';
  const vrParam = params.get('vr');
  gameContext.immersive = vrParam === '1' || vrParam === 'true';

  // Calibration sync. ?profile= adopts a code permanently; otherwise the device
  // reuses its own, minting one the first time. Local storage is written either
  // way — the code only adds durability and the ability to carry a calibration
  // between the hosted build and FUSE Link's localhost origin.
  gameContext.calibrationStore = new CalibrationStore({
    profile: resolveProfileCode(params.get('profile')),
  });
  const qualityParam = params.get('quality');
  if (qualityParam && gameContext.setupData) {
    gameContext.setupData.qualityLevel = parseInt(qualityParam, 10);
  }
  // No host app supplies setup on the headset — let the URL override the
  // defaults (elevation feeds air density; a Denver user needs this)
  const unitsParam = params.get('units');
  if ((unitsParam === 'imperial' || unitsParam === 'metric') && gameContext.setupData) {
    gameContext.setupData.units = unitsParam;
  }
  const elevationParam = params.get('elevation');
  if (elevationParam && gameContext.setupData) {
    const elevation = parseFloat(elevationParam);
    if (!Number.isNaN(elevation)) gameContext.setupData.elevation = elevation;
  }
  // Left-handed players get the side panel across the ball, not in the swing —
  // and their club goes on the other controller
  const leftHanded = params.get('hand')?.toUpperCase() === 'LH';
  gameContext.handedSide = leftHanded ? -1 : 1;
  gameContext.clubHand = leftHanded ? 'left' : 'right';
  gameContext.perfHud = params.get('perf') === '1';
  const testShotsParam = params.get('testshots');
  if (testShotsParam === '1' || testShotsParam === 'true') gameContext.testShotsOverride = true;
  else if (testShotsParam === '0' || testShotsParam === 'false') gameContext.testShotsOverride = false;
  // ?tv=0 disables the chase-cam screen, ?tv=1 shows it on every shot
  // Wrist-menu placement — the only way to tune it on the mat without a rebuild
  const wristOffset = parseFloat(params.get('wrist') ?? '');
  if (!Number.isNaN(wristOffset)) gameContext.wristPalmOffset = wristOffset;
  const wristDistal = parseFloat(params.get('wristdistal') ?? '');
  if (!Number.isNaN(wristDistal)) gameContext.wristDistalOffset = wristDistal;
  const tvParam = params.get('tv');
  if (tvParam === '0' || tvParam === 'false') gameContext.tvMode = 'off';
  else if (tvParam === '1' || tvParam === 'true') gameContext.tvMode = 'always';
  gameContext.tvModeFromUrl = gameContext.tvMode;

  // Swing (virtual golf) mode. Applied after setupRange so setMode can sync the
  // visuals in one place rather than every constructor second-guessing the mode.
  // Giving ?mode= at all FORCES that mode — the input-modality auto-switcher is
  // disabled for the session (debug/kiosk escape hatch).
  const modeParam = params.get('mode')?.toLowerCase();
  gameContext.modeForced = modeParam !== null && modeParam !== undefined;
  const requestedMode: RangeMode = modeParam === 'swing' || modeParam === 'virtual' ? 'swing' : 'monitor';
  // ?attach=none|yezro|yezro-y14|custom, or a bare number for a custom length
  const attachParam = params.get('attach')?.toLowerCase();
  if (attachParam) {
    const attachOffset = parseFloat(attachParam);
    if (!Number.isNaN(attachOffset)) {
      // Numeric = butt offset: how far behind the controller the grip top sits
      gameContext.swingAttachment = 'custom';
      gameContext.swingButtOffset = attachOffset;
    } else if (attachParam === 'yezro' || attachParam === 'yezro-y14' || attachParam === 'y14') {
      gameContext.swingAttachment = 'yezro-y14';
    } else if (attachParam in ATTACHMENTS) {
      gameContext.swingAttachment = attachParam as AttachmentId;
    } else {
      console.warn(`Unknown ?attach=${attachParam}, keeping ${gameContext.swingAttachment}`);
    }
  }
  const swingZone = parseFloat(params.get('swingzone') ?? '');
  if (!Number.isNaN(swingZone) && swingZone > 0) gameContext.swingCaptureRadius = swingZone;
  // ?assist=0..1 — 0 flies the raw measurement, 1 is dead straight off the path
  const assistParam = parseFloat(params.get('assist') ?? '');
  if (!Number.isNaN(assistParam)) gameContext.swingAssist = THREE.MathUtils.clamp(assistParam, 0, 1);
  // ?power= — ball-speed multiplier (default 1.15); 1 for true physics
  const powerParam = parseFloat(params.get('power') ?? '');
  if (!Number.isNaN(powerParam)) gameContext.swingPower = THREE.MathUtils.clamp(powerParam, 0.5, 1.6);
  // ?shape= — curvature responsiveness (default 1 = raw D-plane); 0 = dead straight
  const shapeParam = parseFloat(params.get('shape') ?? '');
  if (!Number.isNaN(shapeParam)) gameContext.swingShape = THREE.MathUtils.clamp(shapeParam, 0, 3);
  gameContext.swingDebug = params.get('swingdebug') === '1';
  // Escape hatches if the one-press calibration proves awkward on the mat
  gameContext.swingShaftAxis = parseAxis(params.get('clubaxis'));
  gameContext.swingFaceAxis = parseAxis(params.get('clubface'));

  // Canvas HUDs measure and draw text at construction — Rubik must be loaded
  // by then or every tile renders in the fallback font
  await Promise.allSettled([
    document.fonts.load('bold 58px "Rubik"'),
    document.fonts.load('bold 52px "Rubik"'),
    document.fonts.load('bold 36px "Rubik"'),
    document.fonts.load('bold 30px "Rubik"'),
    // The help panel's body text is Regular/Medium — a different FACE, not just
    // a different size, so loading only bold would silently fall back
    document.fonts.load('400 30px "Rubik"'),
    document.fonts.load('500 30px "Rubik"'),
  ]);
  if (params.get('mask') === 'dither') {
    gameContext.maskMode = 'dither'; // fallback masking, no compositor-alpha dependency
  }
  // ?wind=mph[,deg] — 0° tail, 180° into, 90° pushes right. Physics-level:
  // drag and lift act on air-relative velocity, so this shapes real flight.
  const windParam = params.get('wind');
  if (windParam) {
    const [mph, deg] = windParam.split(',').map(part => parseFloat(part));
    if (Number.isFinite(mph) && mph >= 0) {
      gameContext.windMph = Math.min(mph, 40);
      gameContext.windDeg = Number.isFinite(deg) ? deg : 0;
    }
  }
  // ?window=off → infinite portal; ?window=dist,left,right,top → manual frame;
  // absent → auto-frame from Space Setup planes (silently infinite without them)
  const windowParam = params.get('window');
  if (windowParam === 'off' || windowParam === '0') {
    gameContext.windowMode = 'off';
  } else if (windowParam) {
    const parts = windowParam.split(',').map(part => parseFloat(part));
    if (parts.length === 4 && parts.every(value => Number.isFinite(value) && value > 0)) {
      gameContext.windowMode = 'manual';
      gameContext.windowRect = { dist: parts[0], left: parts[1], right: parts[2], top: parts[3] };
    } else {
      console.warn(`Ignoring malformed ?window='${windowParam}' — expected dist,left,right,top`);
    }
  }

  await setupRange();

  if (gameContext.simMode) {
    // Fake anchor at the sim origin: portal and disc render on a normal
    // camera. A colorful page background behind the alpha canvas makes
    // premultiply/halo bugs visible — same compositing math as the XR layer.
    gameContext.worldMask?.update(gameContext.startPoint, new THREE.Vector3(0, 0, 1));
    gameContext.worldMask?.setEnabled(true);
    // Window preview: a fake room so the parallax is testable on the desktop
    // (the camera is the eye; orbit it and the opening should shift)
    if (gameContext.windowMode !== 'off') {
      applyWindow(gameContext.windowRect ?? { dist: 3.5, left: 2.2, right: 2.2, top: 2.4 });
    }
    gameContext.statsPanel?.placeAtTee(gameContext.startPoint, 0, gameContext.handedSide);
    gameContext.groundHud?.placeAtTee(gameContext.startPoint, 0);
    const canvas = document.getElementById('canvas') as HTMLCanvasElement;
    canvas.style.background = 'linear-gradient(135deg, #7752c4 0%, #d4525e 50%, #52d4a8 100%)';
    if (statusEl()) statusEl()!.textContent = 'Desktop mask preview (?sim=1) — keys: [ ] portal, - = disc, N window, X colorize';
  }

  setMode(requestedMode);
  applyWind();

  // Bridge URL: ?bridge= wins and is REMEMBERED, so the hosted page keeps
  // reaching the launch monitor on later visits without retyping the param.
  // The default (FUSE Link loopback) only applies when nothing was ever given;
  // ?bridge=default forgets a stored URL and returns to it.
  const bridgeParam = params.get('bridge');
  if (bridgeParam === 'default' || bridgeParam === 'off') {
    try { localStorage.removeItem('fuse.bridgeUrl'); } catch { /* storage disabled */ }
  } else if (bridgeParam) {
    gameContext.bridgeUrl = bridgeParam;
    try { localStorage.setItem('fuse.bridgeUrl', bridgeParam); } catch { /* storage disabled */ }
  } else {
    let stored: string | null = null;
    try { stored = localStorage.getItem('fuse.bridgeUrl'); } catch { /* storage disabled */ }
    if (stored) gameContext.bridgeUrl = stored;
  }
  app.on('bridgeStatus', connected => {
    gameContext.bridgeConnected = connected;
    if (connected) gameContext.bridgeEverConnected = true;
    refreshMonitorStatus();
  });
  app.connectWebSocket(gameContext.bridgeUrl);
  refreshMonitorStatus();

  document.body.style.opacity = '1';
  // setAnimationLoop behaves like rAF outside a session and forwards the
  // XRFrame while presenting — one loop for desktop preview and headset
  gameContext.renderer!.renderer.setAnimationLoop(animate);

  // Only enable Enter MR once the loop exists — a session started earlier
  // would capture a null animation loop and render nothing
  const button = document.getElementById('enter-mr') as HTMLButtonElement | null;
  const xrMode = gameContext.immersive ? 'immersive-vr' : 'immersive-ar';
  const supported = await FuseXRSession.isSupported(xrMode);
  // The in-session switch returns here, so the button has to say which one it
  // is about to start — the markup's default only covers passthrough
  if (button && gameContext.immersive) button.textContent = 'Enter VR';

  // The profile code, on the flat page because that is where it can be copied.
  // Reading a code off a panel inside a session and typing it into another
  // device is the awkward path; this is the one that isn't.
  const profileEl = document.getElementById('mr-profile');
  const store = gameContext.calibrationStore;
  if (profileEl && store?.isRemote) {
    const url = new URL(window.location.href);
    url.searchParams.set('profile', store.profile);
    profileEl.textContent = `Calibration profile ${store.profile}`
      + ' — open this URL on another device to share your club setup:'
      + ` ${url.toString()}`;
  }
  if (!gameContext.simMode && statusEl()) {
    statusEl()!.textContent = supported
      ? 'WebXR ready'
      : `${xrMode} not available — desktop preview only (?sim=1)`;
  }
  if (button) button.disabled = !supported;
}

/** Any setup failure must surface — a blank page in a headset is undebuggable. */
function startApp() {
  preLoad().catch(error => {
    console.error('FUSE MR failed to start', error);
    document.body.style.opacity = '1';
    if (statusEl()) statusEl()!.textContent = `Failed to start: ${error?.message ?? error}`;
  });
}

function initializeSetup(payload: any) {
  if (!payload?.setupData) throw new Error('No setupData received in setup event!');
  gameContext.setupData = payload.setupData as OpenGolfSim.SetupData;
  startApp();
}

function initializeDebug() {
  gameContext.setupData = generateSetupData(1);
  startApp();
}

app.initialize(() => {
  if (app.appType === 'web') {
    initializeDebug();
  } else {
    // Embedded (webview/iframe) hosts must send a setup message; don't hang
    // forever on a blank page if it never arrives
    setTimeout(() => {
      if (!gameContext.setupData) {
        console.warn('No setup message received — starting with defaults');
        initializeDebug();
      }
    }, 4000);
  }
});

app.on('setup', initializeSetup);
// AppBridge 'shot' events on this page come from the launch-monitor bridge —
// real balls cut an in-flight sim short rather than being dropped
app.on('shot', shot => {
  // Swing mode hits a virtual ball on the tee. A monitor reporting at the same
  // time would launch a phantom second shot, so its input is ignored until the
  // player switches back — nothing is lost, the mode toggle is one poke away.
  if (isSwingMode()) return;
  // First real shot proves a monitor is live → practice pinches lock out
  if (!gameContext.monitorLive) {
    gameContext.monitorLive = true;
    refreshMonitorStatus();
    if (gameContext.anchorController?.isAnchored) gameContext.hint?.setText(readyHintText());
  }
  launchShot(shot, 'bridge');
});
